/* ==========================================================================
   V/Cert behaviours
   --------------------------------------------------------------------------
   Shared script for the V/Cert components (see analysis/home). Loaded once,
   deferred, from layout/theme.liquid. Modules are appended in implementation
   order, mirroring assets/vcert.css.

   No dependency. Does not touch assets/theme.js, the Impulse bundle, or any
   existing global.
   ========================================================================== */

(function () {
  'use strict';

  var SCROLL_DEBOUNCE = 120;
  var RESIZE_DEBOUNCE = 150;

  // How close to the bottom edge a fixed overlay has to sit before the
  // back-to-top button (C14) treats it as being in the way.
  var BTT_BOTTOM_TOLERANCE = 8;

  function debounce(fn, wait) {
    var timer;
    return function () {
      window.clearTimeout(timer);
      timer = window.setTimeout(fn, wait);
    };
  }

  /* ------------------------------------------------------------------------
     C02 — Carousel primitive
     Markup: snippets/vcert-carousel.liquid   Styling: assets/vcert.css

     The rail is a native scroll container, so it already works without this
     script. All this adds is the prev/next buttons and the progress state.

     Diverges from the reference prototype in four approved ways (decisions.md
     D9): motion honours prefers-reduced-motion, the rail clamps at both ends
     instead of wrapping, off-screen items are not marked inert, and there is
     no mouse drag-to-scroll.
     ---------------------------------------------------------------------- */

  var instances = [];

  function prefersReducedMotion() {
    return typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function initCarousel(root) {
    if (root.getAttribute('data-vc-carousel-ready') === 'true') return;

    var track = root.querySelector('[data-vc-carousel-track]');
    var prev = root.querySelector('[data-vc-carousel-prev]');
    var next = root.querySelector('[data-vc-carousel-next]');
    if (!track || !prev || !next) return;

    var items = Array.prototype.filter.call(track.children, function (el) {
      return el.tagName === 'LI';
    });
    if (items.length < 2) return;

    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-vc-carousel-dot]'));

    root.setAttribute('data-vc-carousel-ready', 'true');

    var index = 0;
    var perView = 1;
    var pages = 1;

    // False while the rail is display:none — C08 hides its carousel at >=48rem,
    // where the desktop table takes over. Every measurement is worthless then.
    function laidOut() {
      return track.getClientRects().length > 0;
    }

    // Distance from one item to the next, gap included. Taken from the first
    // two items rather than from computed styles so it stays correct whatever
    // the consuming section sets --vc-carousel-item to.
    function stride() {
      return items[1].offsetLeft - items[0].offsetLeft;
    }

    function measure() {
      var styles = window.getComputedStyle(track);
      var padStart = parseFloat(styles.paddingInlineStart || styles.paddingLeft) || 0;
      var padEnd = parseFloat(styles.paddingInlineEnd || styles.paddingRight) || 0;
      var gap = parseFloat(styles.columnGap) || 0;
      var inner = track.clientWidth - padStart - padEnd;
      var step = stride();

      perView = step > 0 ? Math.max(1, Math.round((inner + gap) / step)) : 1;
      pages = Math.max(1, items.length - perView + 1);
      index = Math.min(index, pages - 1);
    }

    function indexFromScroll() {
      var step = stride();
      if (step <= 0) return 0;
      return Math.min(Math.max(Math.round(track.scrollLeft / step), 0), pages - 1);
    }

    function sync() {
      var navigable = pages > 1;

      // Reveals the buttons and the progress bar. Until this runs, a visitor
      // without JavaScript sees a scrollable rail and no dead controls.
      root.classList.toggle('is-ready', navigable);
      prev.hidden = !navigable;
      next.hidden = !navigable;

      // No disabled state: the rail wraps, so neither end is an end (D115).
      // The only dead control this module can produce is a rail that fits
      // entirely on screen, and that one is hidden outright above.

      dots.forEach(function (dot, i) {
        dot.hidden = i >= pages;
        dot.classList.toggle('is-active', i === index);
      });
    }

    // The rail WRAPS: past the last page comes the first, and back from the
    // first comes the last. Same modulo the reference uses, written so a
    // negative target is handled — JavaScript's % keeps the sign of its left
    // operand, so a bare `target % pages` returns -1 at the first page.
    //
    // measure() runs first and can shrink `pages` (a resize, or a rail that was
    // display: none when it was last measured), so the modulo is taken against
    // the page count that is true right now, never a stale one.
    function goTo(target, behavior) {
      if (!laidOut()) return;
      measure();
      index = ((target % pages) + pages) % pages;
      track.scrollTo({
        left: items[index].offsetLeft - items[0].offsetLeft,
        behavior: behavior
      });
      sync();
    }

    prev.addEventListener('click', function () {
      goTo(index - 1, prefersReducedMotion() ? 'auto' : 'smooth');
    });

    next.addEventListener('click', function () {
      goTo(index + 1, prefersReducedMotion() ? 'auto' : 'smooth');
    });

    // Keeps the progress state honest when the rail is scrolled directly —
    // trackpad, shift+wheel, touch, or the browser scrolling a focused item
    // into view.
    track.addEventListener('scroll', debounce(function () {
      if (!laidOut()) return;
      index = indexFromScroll();
      sync();
    }, SCROLL_DEBOUNCE), { passive: true });

    instances.push({
      root: root,
      resize: function () {
        if (!laidOut()) return;
        goTo(index, 'auto');
      }
    });

    if (laidOut()) {
      measure();
      index = indexFromScroll();
    }
    sync();
  }

  function initCarousels(scope) {
    var parent = scope && scope.querySelectorAll ? scope : document;
    Array.prototype.forEach.call(
      parent.querySelectorAll('[data-vc-carousel]'),
      initCarousel
    );
  }

  // One window listener for every carousel on the page, pruning instances the
  // theme editor has removed from the document.
  window.addEventListener('resize', debounce(function () {
    instances = instances.filter(function (instance) {
      return instance.root.isConnected;
    });
    instances.forEach(function (instance) {
      instance.resize();
    });
  }, RESIZE_DEBOUNCE));

  /* ------------------------------------------------------------------------
     C09 — Stat count-up
     Markup: sections/vcert-trust.liquid   Styling: assets/vcert.css

     The final value is already in the HTML (decisions.md D28), so this module
     is pure enhancement: with JavaScript off, or under prefers-reduced-motion,
     the correct number is on screen and nothing here runs.

     It rewrites only the <span data-vc-stat-num>, never the whole paragraph, so
     the prefix and suffix text nodes survive untouched.
     ---------------------------------------------------------------------- */

  var STAT_DURATION = 1600;
  var STAT_THRESHOLD = 0.4;

  // The merchant authors the value as text, so the separators are whatever they
  // typed. A comma is unambiguous: decimal. A single trailing dot group of one
  // or two digits is read as decimal too; anything else (1.500) is grouping.
  //
  // A mis-read is survivable by construction: only the intermediate frames come
  // from this, and the last frame restores the authored string verbatim.
  function readStat(raw) {
    var source = String(raw == null ? '' : raw);

    // A negative value would count up from 0 to its absolute value and then
    // snap to the authored minus sign on the last frame. Nothing to animate.
    if (source.indexOf('-') > -1) return null;

    var s = source.replace(/[^0-9.,]/g, '');
    if (!s) return null;

    var decimals = 0;
    var normalized = s;
    var comma = s.lastIndexOf(',');

    if (comma > -1) {
      decimals = s.length - comma - 1;
      normalized = s.replace(/\./g, '').replace(',', '.');
    } else {
      var dot = s.lastIndexOf('.');
      var after = dot > -1 ? s.length - dot - 1 : 0;
      if (dot > -1 && s.indexOf('.') === dot && after > 0 && after <= 2) {
        decimals = after;
      } else {
        normalized = s.replace(/\./g, '');
      }
    }

    if (decimals > 3) {
      decimals = 0;
      normalized = s.replace(/[.,]/g, '');
    }

    var value = parseFloat(normalized);
    if (!isFinite(value) || value <= 0) return null;
    return { value: value, decimals: decimals };
  }

  var statLocale = document.documentElement.getAttribute('lang') || undefined;

  function formatStat(value, decimals) {
    try {
      return value.toLocaleString(statLocale, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      });
    } catch (error) {
      return value.toFixed(decimals);
    }
  }

  function animateStat(el) {
    var num = el.querySelector('[data-vc-stat-num]');
    if (!num) return;

    var authored = num.textContent;
    var parsed = readStat(el.getAttribute('data-vc-stat-value') || authored);
    if (!parsed) return;

    var started = null;

    function frame(now) {
      if (started === null) started = now;
      var progress = Math.min((now - started) / STAT_DURATION, 1);

      if (progress < 1) {
        var eased = 1 - Math.pow(1 - progress, 3);
        num.textContent = formatStat(parsed.value * eased, parsed.decimals);
        window.requestAnimationFrame(frame);
      } else {
        // Never the formatted number: the authored string is the source of
        // truth and has to be what the visitor is left looking at.
        num.textContent = authored;
      }
    }

    window.requestAnimationFrame(frame);
  }

  function initStats(scope) {
    if (prefersReducedMotion()) return;
    if (typeof window.IntersectionObserver !== 'function') return;
    if (typeof window.requestAnimationFrame !== 'function') return;

    var parent = scope && scope.querySelectorAll ? scope : document;
    var nodes = Array.prototype.filter.call(
      parent.querySelectorAll('[data-vc-stat]'),
      function (el) {
        // 'done' is set when the counter has run; 'watched' when an observer is
        // already on the element. Without the second flag, a section reload in
        // the theme editor while the stat is still below the fold attaches a
        // SECOND observer: both fire on the same intersection and two rAF loops
        // write the same node. The final value survives (both read the same
        // pristine textContent synchronously, before any frame runs), so this
        // is wasted work and visible jitter rather than a wrong number — but it
        // is still work nobody asked for. See Q13.
        return el.getAttribute('data-vc-stat-done') !== 'true'
          && el.getAttribute('data-vc-stat-watched') !== 'true';
      }
    );
    if (!nodes.length) return;

    var observer = new window.IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);
        entry.target.setAttribute('data-vc-stat-done', 'true');
        animateStat(entry.target);
      });
    }, { threshold: STAT_THRESHOLD });

    nodes.forEach(function (el) {
      el.setAttribute('data-vc-stat-watched', 'true');
      observer.observe(el);
    });
  }

  /* ------------------------------------------------------------------------
     C03b — Mega panel popovers
     Markup: sections/vcert-header.liquid   Styling: assets/vcert.css

     The panels are Popover API popovers, so popovertarget already gives click
     open/close, light-dismiss and Escape with no script. This module adds the
     three things it does not:

       1. aria-expanded on the invoker, mirrored from the panel's toggle event.
       2. Pointer behaviour at >=64rem: open on hover, close on a 150ms delay.
          The delay is not decoration — it is what lets the pointer travel from
          the trigger down into the panel without the panel closing underneath
          it.
       3. Keyboard behaviour: Down/Up opens and enters the panel, Left/Right
          moves between top-level triggers, Home/End jump within an open panel.

     Where popover is NOT supported the module drives the panels manually
     instead. The reference ships a polyfill for the same reason: its stated
     audience is accountants on legacy corporate machines.
     ---------------------------------------------------------------------- */

  var SUPPORTS_POPOVER =
    typeof HTMLElement !== 'undefined' &&
    Object.prototype.hasOwnProperty.call(HTMLElement.prototype, 'popover');

  var MEGA_CLOSE_DELAY = 150;

  // Hover is gated on width AND on the pointer actually being a fine hovering
  // one. The reference gates on width alone, which makes a touch device at
  // tablet width fire mouseenter on tap and fight its own click handler.
  function megaPointerNav() {
    return typeof window.matchMedia === 'function' &&
      window.matchMedia('(min-width: 64rem)').matches &&
      window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  }

  function megaInvoker(panel) {
    return document.querySelector('[aria-controls="' + panel.id + '"]');
  }

  function setExpanded(panel, open) {
    var invoker = megaInvoker(panel);
    if (invoker) invoker.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  function megaIsOpen(panel) {
    return SUPPORTS_POPOVER
      ? panel.matches(':popover-open')
      : panel.classList.contains('is-open');
  }

  function openMega(panel) {
    if (megaIsOpen(panel)) return;
    if (SUPPORTS_POPOVER) {
      try {
        panel.showPopover();
      } catch (error) {
        /* already open, or detached */
      }
    } else {
      panel.classList.add('is-open');
      setExpanded(panel, true);
    }
  }

  function closeMega(panel) {
    if (!megaIsOpen(panel)) return;
    if (SUPPORTS_POPOVER) {
      try {
        panel.hidePopover();
      } catch (error) {
        /* already closed */
      }
    } else {
      panel.classList.remove('is-open');
      setExpanded(panel, false);
    }
  }

  function closeAllMegas(except) {
    Array.prototype.forEach.call(document.querySelectorAll('.vc-mega'), function (panel) {
      if (panel !== except) closeMega(panel);
    });
  }

  function megaFocusables(panel) {
    return panel.querySelectorAll('a[href], button:not([disabled])');
  }

  function initMega(panel) {
    if (panel.getAttribute('data-vc-mega-ready') === 'true') return;

    var invoker = megaInvoker(panel);
    if (!invoker) return;
    var item = invoker.closest('.vc-navbar__item');
    if (!item) return;

    panel.setAttribute('data-vc-mega-ready', 'true');

    if (SUPPORTS_POPOVER) {
      panel.addEventListener('toggle', function (event) {
        setExpanded(panel, event.newState === 'open');
      });
    } else {
      // Without popover the panel is an ordinary block in normal flow and would
      // render open, so it starts hidden and .is-open reveals it.
      panel.classList.add('vc-mega--fallback');
      invoker.addEventListener('click', function () {
        var open = megaIsOpen(panel);
        closeAllMegas(panel);
        if (open) {
          closeMega(panel);
          invoker.focus();
        } else {
          openMega(panel);
        }
      });
    }

    /* ---- pointer ---- */

    var closeTimer = null;

    function cancelClose() {
      if (closeTimer) {
        window.clearTimeout(closeTimer);
        closeTimer = null;
      }
    }

    function pointerOpen() {
      if (!megaPointerNav()) return;
      cancelClose();
      closeAllMegas(panel);
      openMega(panel);
    }

    function pointerClose() {
      if (!megaPointerNav()) return;
      cancelClose();
      closeTimer = window.setTimeout(function () {
        closeMega(panel);
      }, MEGA_CLOSE_DELAY);
    }

    item.addEventListener('mouseenter', pointerOpen);
    panel.addEventListener('mouseenter', pointerOpen);
    item.addEventListener('mouseleave', pointerClose);
    panel.addEventListener('mouseleave', pointerClose);

    // Hover has already opened it, so the browser's own popovertarget toggle
    // would close it on the very click that was meant to follow the link.
    // Suppressed only while the pointer is actually over the item, so a click
    // or keyboard user can still toggle normally.
    if (SUPPORTS_POPOVER) {
      invoker.addEventListener('click', function (event) {
        if (megaIsOpen(panel) && megaPointerNav() && item.matches(':hover')) {
          event.preventDefault();
        }
      });
    }

    /* ---- keyboard ---- */

    item.addEventListener('focusout', function (event) {
      var next = event.relatedTarget;
      if (next && item.contains(next)) return;
      closeMega(panel);
    });

    item.addEventListener('keydown', function (event) {
      var key = event.key;
      var triggers = Array.prototype.slice.call(
        document.querySelectorAll('.vc-navbar__toggle')
      );
      var index = triggers.indexOf(invoker);

      if (key === 'ArrowDown' || key === 'ArrowUp') {
        event.preventDefault();
        cancelClose();
        openMega(panel);
        var links = megaFocusables(panel);
        var target = key === 'ArrowDown' ? links[0] : links[links.length - 1];
        if (target) target.focus();
        return;
      }

      if ((key === 'ArrowRight' || key === 'ArrowLeft') && index > -1) {
        event.preventDefault();
        var step = key === 'ArrowRight' ? 1 : -1;
        var next = triggers[(index + step + triggers.length) % triggers.length];
        if (next) next.focus();
      }
    });

    panel.addEventListener('keydown', function (event) {
      if (event.key !== 'Home' && event.key !== 'End') return;
      event.preventDefault();
      var links = megaFocusables(panel);
      var target = event.key === 'Home' ? links[0] : links[links.length - 1];
      if (target) target.focus();
    });
  }

  function initMegas(scope) {
    var parent = scope && scope.querySelectorAll ? scope : document;
    Array.prototype.forEach.call(parent.querySelectorAll('.vc-mega'), initMega);
  }

  // Escape and outside-click are native behaviour for a real popover, so these
  // two listeners are only wired when the fallback is in charge.
  if (!SUPPORTS_POPOVER) {
    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') closeAllMegas();
    });
    document.addEventListener('click', function (event) {
      var target = event.target;
      if (target && target.closest && target.closest('.vc-mega, .vc-navbar__toggle')) return;
      closeAllMegas();
    });
  }

  /* ------------------------------------------------------------------------
     C03c — Mobile drawer
     Markup: sections/vcert-header.liquid   Styling: assets/vcert.css

     A native <dialog>. The browser already provides focus trapping, Escape,
     ::backdrop and inerting the rest of the page, so this module only:

       - opens and closes it,
       - closes it when the backdrop is clicked,
       - mirrors state onto the burger's aria-expanded,
       - returns focus to the burger on close,
       - sets html.is-locked as a reserve for engines without :has().

     showModal() is driven directly rather than through command/commandfor. The
     Invoker Commands API is not Baseline, a fallback would be required anyway,
     and <dialog> itself has been Baseline since 2022 — so the attributes would
     add an unsupported API and a polyfill for it, and buy nothing.
     ---------------------------------------------------------------------- */

  function drawerOf(button) {
    var id = button.getAttribute('aria-controls');
    return id ? document.getElementById(id) : null;
  }

  function setDrawerLock(locked) {
    document.documentElement.classList.toggle('is-locked', locked);
  }

  function initDrawer(button) {
    if (button.getAttribute('data-vc-drawer-ready') === 'true') return;

    var drawer = drawerOf(button);
    if (!drawer || typeof drawer.showModal !== 'function') return;

    button.setAttribute('data-vc-drawer-ready', 'true');

    button.addEventListener('click', function () {
      if (drawer.open) return;
      drawer.showModal();
      button.setAttribute('aria-expanded', 'true');
      setDrawerLock(true);
    });

    Array.prototype.forEach.call(
      drawer.querySelectorAll('[data-vc-drawer-close]'),
      function (closer) {
        closer.addEventListener('click', function () {
          drawer.close();
        });
      }
    );

    // A click that lands on the <dialog> itself is a click on the backdrop:
    // every real control inside is a descendant, so it would be the target
    // instead.
    drawer.addEventListener('click', function (event) {
      if (event.target === drawer) drawer.close();
    });

    // Fires for Escape and for close() alike, so state is restored on every
    // route out of the dialog rather than only the ones we handle.
    drawer.addEventListener('close', function () {
      button.setAttribute('aria-expanded', 'false');
      setDrawerLock(false);
      button.focus();
    });
  }

  /* ------------------------------------------------------------------------
     C03d — Search panel focus
     Markup: sections/vcert-header.liquid   Styling: assets/vcert.css

     The search panel is a popover with the same geometry as the mega panel, so
     initMegas already drives it. All that is added here is moving focus into the
     field on open — the reference does not, which costs the user a Tab.

     The cart trigger used to live here too, dispatching `toggle-cart` at the
     EvolveHub minicart. It moved to C17 below when the theme took the cart back
     off the app (D117).
     ---------------------------------------------------------------------- */

  function initSearchPanel(panel) {
    if (panel.getAttribute('data-vc-search-ready') === 'true') return;
    panel.setAttribute('data-vc-search-ready', 'true');

    panel.addEventListener('toggle', function (event) {
      if (event.newState !== 'open') return;
      var field = panel.querySelector('input[type="search"]');
      if (field) field.focus();
    });

    // The clear button is a type="reset" that hides itself once the field is
    // empty; without this, focus would be left on a hidden control. The
    // reset event fires before the form is cleared, hence the frame.
    panel.addEventListener('reset', function () {
      var field = panel.querySelector('input[type="search"]');
      if (field) requestAnimationFrame(function () { field.focus(); });
    });
  }

  function initSearchPanels(scope) {
    var parent = scope && scope.querySelectorAll ? scope : document;
    Array.prototype.forEach.call(
      parent.querySelectorAll('.vc-searchpop'),
      initSearchPanel
    );
  }

  /* ------------------------------------------------------------------------
     C14 — Back-to-top
     Markup: sections/vcert-back-to-top.liquid   Styling: assets/vcert.css

     Reveals the button past a threshold and scrolls back to the top. Two
     details are worth the words:

     1. SCROLL WORK IS COALESCED INTO A rAF. The listener is passive and only
        flips a class, but it fires at scroll frequency, so it sets a flag and
        lets one rAF do the DOM work. debounce() is not used here: a debounced
        reveal would lag visibly behind the scroll it is reacting to.

     2. THE OVERLAY IS MEASURED, NOT ASSUMED. data-vc-btt-clear names an element
        (this store: the cookie-consent banner) whose height is added to the
        button's offset while it is on screen, so the button never hides behind
        it. The selector comes from the markup, never from this file — the
        script must not know which consent app the store happens to use.
        Measured on resize and whenever visibility flips, not per frame.
     ---------------------------------------------------------------------- */

  function bttClearNodes(button) {
    var selector = button.getAttribute('data-vc-btt-clear');
    if (!selector) return [];

    try {
      // querySelectorAll, not querySelector. The product page has TWO overlays
      // pinned to the bottom - the consent banner and the P08 purchase bar -
      // and querySelector returns whichever comes first in the DOM, which is
      // not the one the button has to clear. Both are measured and the taller
      // wins below; they overlap rather than stack, so this is a max and not a
      // sum.
      return Array.prototype.slice.call(document.querySelectorAll(selector));
    } catch (error) {
      // A merchant typed something that is not a valid selector. The button
      // keeps its base offset rather than throwing and taking the module down.
      return [];
    }
  }

  function bttClearHeight(node) {
    // getClientRects().length is 0 for display:none, which getBoundingClientRect
    // cannot distinguish from a genuinely zero-height element.
    if (!node || node.getClientRects().length === 0) return 0;

    var rect = node.getBoundingClientRect();

    // documentElement.clientHeight, NOT window.innerHeight. innerHeight includes
    // the horizontal scrollbar, so on a page that scrolls sideways the two differ
    // by the scrollbar width and an overlay genuinely pinned to the bottom reads
    // as "not at the bottom". That is a real bug this check had, found by
    // measuring the live page: the button sat 16px INSIDE the consent banner.
    var viewportHeight = document.documentElement.clientHeight || window.innerHeight;
    if (rect.bottom < viewportHeight - BTT_BOTTOM_TOLERANCE) return 0;
    return Math.max(0, Math.round(rect.height));
  }

  function initBackToTop(button) {
    if (button.getAttribute('data-vc-btt-ready') === 'true') return;
    button.setAttribute('data-vc-btt-ready', 'true');

    var threshold = parseInt(button.getAttribute('data-vc-btt-threshold'), 10);
    if (!threshold || threshold < 0) threshold = 600;

    var visible = null;
    var ticking = false;
    var observedNodes = [];
    var clearObserver = null;

    function syncClearance() {
      var nodes = bttClearNodes(button);

      // Observed as nodes APPEAR, not once at the first find. The consent
      // banner is injected by a third-party script about 1.5s in, while the
      // purchase bar is in the server response — so a latch that closes on the
      // first node found would close on the bar and never observe the banner.
      // Measured: --vc-btt-clear stayed at the bar's 69px while a 288px banner
      // sat on top of the button. That is the regression this loop exists to
      // prevent, and it was introduced by widening the selector to two nodes.
      if (nodes.length && typeof window.ResizeObserver === 'function') {
        if (!clearObserver) clearObserver = new window.ResizeObserver(syncClearance);
        for (var j = 0; j < nodes.length; j++) {
          if (observedNodes.indexOf(nodes[j]) !== -1) continue;
          observedNodes.push(nodes[j]);
          clearObserver.observe(nodes[j]);
        }
      }

      var height = 0;
      for (var i = 0; i < nodes.length; i++) {
        height = Math.max(height, bttClearHeight(nodes[i]));
      }
      if (height > 0) {
        button.style.setProperty('--vc-btt-clear', height + 'px');
      } else {
        button.style.removeProperty('--vc-btt-clear');
      }
    }

    function apply() {
      ticking = false;
      var next = window.pageYOffset > threshold;
      if (next === visible) return;
      visible = next;
      button.classList.toggle('is-visible', next);
      if (next) syncClearance();
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(apply);
    }

    button.addEventListener('click', function () {
      window.scrollTo({
        top: 0,
        behavior: prefersReducedMotion() ? 'auto' : 'smooth'
      });
    });

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', debounce(syncClearance, RESIZE_DEBOUNCE));

    // 'load' catches an overlay that arrives after this module runs. If the node
    // is removed from the DOM outright rather than hidden, the observer stops
    // firing and the offset corrects on the next reveal or resize.
    window.addEventListener('load', syncClearance);

    apply();
    syncClearance();
  }

  function initBackToTops(scope) {
    var parent = scope && scope.querySelectorAll ? scope : document;
    Array.prototype.forEach.call(parent.querySelectorAll('[data-vc-btt]'), initBackToTop);
  }

  /* ------------------------------------------------------------------------
     C13 — Exit-intent coupon banner
     Markup: sections/vcert-exit-banner.liquid   Styling: assets/vcert.css

     The dialog itself needs no script: showModal() traps focus, Escape closes,
     ::backdrop paints. This module only decides WHEN to open it, and copies the
     code.

     Four things here differ from the reference prototype, all deliberate
     (decisions.md D59):

     1. The automatic open is fine-pointer only unless data-vc-xb-mobile says
        otherwise. An interstitial on a phone is a UX and SEO liability.
     2. It never opens while the consent banner is on screen. The trigger
        DEFERS, it does not cancel: the listeners stay attached, so the visitor
        can still be offered the coupon once they have answered.
     3. The coupon code is not replaced by "Copiado!" — only the button label
        changes, and the result is announced through a live region.
     4. A copy that fails says so, rather than reporting success.
     ---------------------------------------------------------------------- */

  var XB_SCROLL_UP_MIN = 60;
  var XB_SCROLL_TOP_MAX = 150;

  function xbSeen(key) {
    try {
      return window.sessionStorage.getItem(key) === '1';
    } catch (error) {
      // Private mode, or storage blocked. Treat as unseen: at worst the banner
      // opens once more than intended, which beats throwing on page load.
      return false;
    }
  }

  function xbMarkSeen(key) {
    try {
      window.sessionStorage.setItem(key, '1');
    } catch (error) {
      /* nothing to do; see xbSeen */
    }
  }

  function xbBlockedByConsent(dialog) {
    // C18 holds the page while ANY consent dialog is up — including the
    // preferences panel, which the selector below does not name. showModal()
    // over it would inert the consent UI itself.
    if (document.documentElement.classList.contains('vc-consent-open')) return true;

    var selector = dialog.getAttribute('data-vc-xb-consent');
    if (!selector) return false;

    var node;
    try {
      node = document.querySelector(selector);
    } catch (error) {
      return false;
    }
    return !!node && node.getClientRects().length > 0;
  }

  function initExitBanner(dialog) {
    if (dialog.getAttribute('data-vc-xb-ready') === 'true') return;
    dialog.setAttribute('data-vc-xb-ready', 'true');

    if (typeof dialog.showModal !== 'function') return;

    var key = dialog.getAttribute('data-vc-xb-key') || 'vc-xb';
    var storageKey = 'vcert-exit-intent-seen:' + key;
    var status = dialog.querySelector('[data-vc-xb-status]');
    var copyButtons = dialog.querySelectorAll('[data-vc-xb-copy]');
    var lastScroll = window.pageYOffset;
    var timer = null;
    var copyTimer = null;

    function teardown() {
      document.removeEventListener('mouseleave', onMouseLeave);
      window.removeEventListener('scroll', onScrollUp);
      window.clearTimeout(timer);
    }

    function open() {
      if (dialog.open || xbSeen(storageKey)) return;
      // Checked at trigger time, not at init: the consent banner is injected by
      // an async third-party script and is answered while the page is open.
      if (xbBlockedByConsent(dialog)) return;

      xbMarkSeen(storageKey);
      dialog.showModal();
      teardown();
    }

    function close() {
      if (dialog.open) dialog.close();
    }

    function onMouseLeave(event) {
      if (event.clientY <= 0) open();
    }

    function onScrollUp() {
      var current = window.pageYOffset;
      var rise = lastScroll - current;
      if (current < XB_SCROLL_TOP_MAX && rise > XB_SCROLL_UP_MIN) open();
      lastScroll = current;
    }

    // Clicking the backdrop lands on the dialog element itself, never on its
    // children, which is what makes this two-line dismissal correct.
    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) close();
    });

    var closeButton = dialog.querySelector('[data-vc-xb-close]');
    if (closeButton) closeButton.addEventListener('click', close);

    Array.prototype.forEach.call(copyButtons, function (button) {
      button.addEventListener('click', function () {
        var code = button.getAttribute('data-vc-xb-code') || '';
        if (!code) return;

        var label = button.hasAttribute('data-vc-xb-label') ? button : null;
        var original = label ? label.textContent : '';
        var copied = label ? (label.getAttribute('data-vc-xb-copied') || original) : '';

        function report(message, restore) {
          if (status) status.textContent = message;
          if (label && restore) label.textContent = restore;
          window.clearTimeout(copyTimer);
          copyTimer = window.setTimeout(function () {
            if (label) label.textContent = original;
            if (status) status.textContent = '';
          }, 2000);
        }

        // navigator.clipboard is undefined outside a secure context. Saying
        // nothing is better than a label that claims a copy that never happened;
        // the code stays on screen and selectable either way.
        if (!navigator.clipboard || typeof navigator.clipboard.writeText !== 'function') {
          report(code, null);
          return;
        }

        navigator.clipboard.writeText(code).then(function () {
          report(copied || code, copied);
        }).catch(function () {
          report(code, null);
        });
      });
    });

    // An in-page CTA has to close the dialog before scrolling, or the scroll
    // happens behind a modal that is still trapping focus.
    var cta = dialog.querySelector('[data-vc-xb-cta]');
    if (cta) {
      cta.addEventListener('click', function (event) {
        var href = cta.getAttribute('href') || '';
        if (href.charAt(0) !== '#' || href.length < 2) return;

        var target = document.getElementById(href.slice(1));
        if (!target) return;

        event.preventDefault();
        close();
        target.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
      });
    }

    if (xbSeen(storageKey)) return;

    var coarse = typeof window.matchMedia === 'function' &&
      window.matchMedia('(pointer: coarse)').matches;

    if (coarse) {
      if (dialog.getAttribute('data-vc-xb-mobile') !== 'true') return;

      var delay = parseInt(dialog.getAttribute('data-vc-xb-delay'), 10);
      if (!delay || delay < 1) delay = 30;
      timer = window.setTimeout(open, delay * 1000);
      window.addEventListener('scroll', onScrollUp, { passive: true });
    } else {
      document.addEventListener('mouseleave', onMouseLeave);
    }
  }

  function initExitBanners(scope) {
    var parent = scope && scope.querySelectorAll ? scope : document;
    Array.prototype.forEach.call(parent.querySelectorAll('[data-vc-xb]'), initExitBanner);
  }

  function initDrawers(scope) {
    var parent = scope && scope.querySelectorAll ? scope : document;
    Array.prototype.forEach.call(
      parent.querySelectorAll('[data-vc-drawer-open]'),
      initDrawer
    );
  }

  /* ------------------------------------------------------------------------
     C17 — Cart modal
     Markup: sections/vcert-cart-modal.liquid   Styling: assets/vcert.css

     Replaces the EvolveHub minicart on the V/Cert templates (decisions.md
     D117). Everywhere else in the store the app is untouched, and the way this
     module stays out of its way is worth stating precisely, because nothing in
     the markup announces it:

       cartRoot() is null on every non-V/Cert template, because the section is
       only rendered behind the layout's is_vcert_template guard. Every entry
       point below returns early when it is null, so on those pages the header
       link navigates, the forms post to the app, and this module does nothing.

     WHY THE SUBMIT LISTENER IS IN THE CAPTURE PHASE

     assets/custom.js:77 binds its own submit listener to every
     [action="/cart/add"] form at DOMContentLoaded and hands the add to
     EvolveCore. It cannot be reasoned with from the outside and editing it
     would change the cart on templates that have not been redesigned yet.

     A listener on the DOCUMENT in the capture phase runs BEFORE any listener on
     the form itself, so stopPropagation() there keeps the event from reaching
     it at all. The app keeps its forms; we take the ones on our pages.

     WHICH SUBMITS ARE OURS

     Only event.submitter.name === 'add'. The buybox posts the same form from
     two buttons (snippets/vcert-buybox.liquid): "Comprar" carries
     name="return_to" value="/checkout" and has to keep posting natively, or
     buy-now stops reaching the checkout. custom.js does NOT make that
     distinction today — it intercepts both and rebuilds the body from
     FormData(form), which drops the submitter's value, so buy-now currently
     lands in the app drawer instead of the checkout. That is fixed here by
     construction, on these templates.

     WHAT HAPPENS WHEN IT FAILS

     Every failure path ends in a real navigation, never in a dead control:
     a failed add re-posts the form for real (the visitor gets the cart page, or
     Shopify's own error for it — exactly the no-JavaScript outcome), and a
     failed quantity change re-reads the section, then gives up to /cart. The
     panel is never left showing a cart Shopify no longer has.
     ---------------------------------------------------------------------- */

  var CART_SECTION = 'vcert-cart-modal';

  // One request at a time. Each control posts an ABSOLUTE target quantity
  // (see the section's comment), so a double click cannot compound — but two
  // in-flight writes can still land out of order, and the second render would
  // then show the first one's cart.
  var cartBusy = false;

  // The control that opened the panel, so focus can go back to it on close.
  var cartInvoker = null;

  function cartRoot() {
    return document.querySelector('[data-vc-cart]');
  }

  /** Shopify's own root, so the cart routes stay correct under a locale prefix. */
  function shopifyRoot() {
    var routes = window.Shopify && window.Shopify.routes;
    return (routes && routes.root) || '/';
  }

  function cartIsOpen(root) {
    try {
      return root.matches(':popover-open');
    } catch (error) {
      return false;
    }
  }

  function openCart(invoker) {
    var root = cartRoot();
    if (!root || typeof root.showPopover !== 'function') return false;
    if (invoker) cartInvoker = invoker;
    if (cartIsOpen(root)) return true;
    try {
      root.showPopover();
    } catch (error) {
      return false;
    }
    return true;
  }

  /* Focus is moved INTO the panel on open, which the reference does not do.
     Same call made for the search popover in C03d: a panel that opens behind
     the keyboard is a panel a keyboard user has to go looking for. */
  function focusCart() {
    var root = cartRoot();
    if (!root || !cartIsOpen(root)) return;
    var close = root.querySelector('[data-vc-cart-close]');
    if (close) close.focus();
  }

  function setCartBusy(state) {
    var root = cartRoot();
    cartBusy = state;
    if (!root) return;
    if (state) root.setAttribute('aria-busy', 'true');
    else root.removeAttribute('aria-busy');
  }

  /** Every [data-cart-count] in the header, from the number the server rendered. */
  function syncCartCount() {
    var root = cartRoot();
    if (!root) return;
    var count = root.getAttribute('data-vc-cart-count') || '0';
    Array.prototype.forEach.call(
      document.querySelectorAll('[data-cart-count]'),
      function (el) { el.textContent = count; }
    );
  }

  /* The control that was just clicked has been replaced, so focus has fallen to
     <body> and the panel is gone from the keyboard's point of view. Put it back
     on the same control if its line survived, on the close button if it did
     not — which is the case after a remove, and after a "−" that reached 0. */
  function restoreCartFocus(root, line, role) {
    if (!line || !role || !cartIsOpen(root)) return;
    var target = null;
    // Shopify line keys are digits, a colon and hex. Anything else did not come
    // from our own markup, so it is not going into a selector.
    if (role !== 'remove' && /^[\w:.-]+$/.test(line)) {
      target = root.querySelector(
        '[data-vc-cart-line="' + line + '"] [data-vc-cart-role="' + role + '"]'
      );
    }
    if (!target) target = root.querySelector('[data-vc-cart-close]');
    if (target) target.focus();
  }

  /* Only the INNER html is replaced. Removing the popover element itself would
     take it out of the top layer and shut the panel on every click. */
  function applyCart(html, line, role) {
    var root = cartRoot();
    if (!root) return;
    var next = new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector('[data-vc-cart]');
    if (!next) throw new Error('cart section rendered without its root');

    root.innerHTML = next.innerHTML;
    root.setAttribute('data-vc-cart-count', next.getAttribute('data-vc-cart-count') || '0');
    syncCartCount();
    restoreCartFocus(root, line, role);
  }

  function cartSectionUrl() {
    var search = window.location.search;
    return window.location.pathname +
      (search ? search + '&' : '?') + 'section_id=' + CART_SECTION;
  }

  /* Fallback for a cart response that came back without the section — and the
     recovery path for a change that failed. One extra round trip, only when the
     bundled render did not arrive. */
  function refetchCart(line, role) {
    return window.fetch(cartSectionUrl(), { headers: { Accept: 'text/html' } })
      .then(function (response) {
        if (!response.ok) throw new Error('cart section ' + response.status);
        return response.text();
      })
      .then(function (html) { applyCart(html, line, role); });
  }

  function renderCart(data, line, role) {
    var html = data && data.sections && data.sections[CART_SECTION];
    if (!html) return refetchCart(line, role);
    applyCart(html, line, role);
    return null;
  }

  function changeCartLine(key, quantity, role) {
    setCartBusy(true);

    window.fetch(shopifyRoot() + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity, sections: CART_SECTION })
    })
      .then(function (response) {
        if (!response.ok) throw new Error('cart change ' + response.status);
        return response.json();
      })
      .then(function (data) { return renderCart(data, key, role); })
      .catch(function () {
        return refetchCart(key, role).catch(function () {
          window.location.href = shopifyRoot() + 'cart';
        });
      })
      .then(function () { setCartBusy(false); });
  }

  function onCartClick(event) {
    var root = cartRoot();
    if (!root || !root.contains(event.target)) return;
    if (!event.target.closest) return;

    var control = event.target.closest('[data-vc-cart-change]');
    if (!control || !root.contains(control)) return;

    var item = control.closest('[data-vc-cart-line]');
    if (!item) return;

    event.preventDefault();
    if (cartBusy) return;

    var quantity = parseInt(control.getAttribute('data-vc-cart-change'), 10);
    if (isNaN(quantity) || quantity < 0) return;

    changeCartLine(
      item.getAttribute('data-vc-cart-line'),
      quantity,
      control.getAttribute('data-vc-cart-role')
    );
  }

  function onCartFormSubmit(event) {
    var form = event.target;
    if (!form || form.tagName !== 'FORM') return;
    if (!/\/cart\/add\b/.test(form.getAttribute('action') || '')) return;

    // No submitter means we cannot tell "Comprar" from "Adicionar", so we stay
    // out and the form posts. Same for a browser without fetch or FormData —
    // and custom.js, which is written in async/await, is not registered in one.
    var submitter = event.submitter;
    if (!submitter || submitter.name !== 'add') return;

    var root = cartRoot();
    if (!root || typeof window.fetch !== 'function' || typeof window.FormData !== 'function') return;

    event.preventDefault();
    event.stopPropagation();

    // Swallowed, not released: returning before preventDefault would let the
    // second half of a double click post the form for real and navigate away
    // from the panel the first half had just opened.
    if (cartBusy) return;
    setCartBusy(true);

    // The form's own body, posted as-is. Rebuilding it as JSON would have to
    // re-encode properties[...] and selling plans by hand, and drop whatever a
    // future field adds.
    var body = new FormData(form);
    body.append('sections', CART_SECTION);

    window.fetch(shopifyRoot() + 'cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: body
    })
      .then(function (response) {
        if (!response.ok) throw new Error('cart add ' + response.status);
        return response.json();
      })
      .then(function (data) { return renderCart(data); })
      .then(function () {
        setCartBusy(false);
        openCart(submitter);
        focusCart();
      })
      .catch(function () {
        setCartBusy(false);
        form.submit();
      });
  }

  /* ------------------------------------------------------------------------
     Checkout routing — "Roteamento de compra" (decisions.md D124)

     The two routes to a checkout on these templates are native form posts:
     the panel's "Finalizar compra" (POST /cart, name="checkout") and the
     buybox's "Comprar", also on the sticky bar (POST /cart/add,
     return_to=/checkout). Both land on Shopify's checkout. With the setting on
     "Checkout BUDA" they are taken over here and sent where the EvolveHub app
     sends them everywhere else in the store:

       <domain>/Arrival/<cart token>[?key=...]&discount=...&<visit UTMs>

     The token alone is enough. Measured in production before this was written:
     the Buda checkout creates its session from the token server-side, with no
     app loaded and no SaveCheckoutSession snapshot, items and totals matching
     the Shopify cart (D124).

     The setting is read at submit time from a JSON block the layout renders.
     No block, "Checkout SHOPIFY" or a blank domain: nothing here runs and the
     forms post natively, which is also the no-JavaScript outcome.

     Every failure falls back to Shopify's checkout, never to a dead control.
     "Comprar" distinguishes a failed add (re-post the form, return_to intact)
     from a failure after it (go to /checkout; re-posting would add it twice).
     ---------------------------------------------------------------------- */

  var checkoutBusy = false;

  /** The Buda domain when the store routes there, else null (native checkout). */
  function budaCheckoutBase() {
    var node = document.getElementById('vc-checkout-routing');
    if (!node) return null;
    try {
      var config = JSON.parse(node.textContent);
      if (!config || config.routing !== 'buda' || !config.budaUrl) return null;
      return String(config.budaUrl).replace(/\/+$/, '');
    } catch (error) {
      return null;
    }
  }

  function readCookie(name) {
    var match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return match ? match[1] : null;
  }

  /* The query the app appends: the automatic discount cookie, then the visit's
     tracking parameters (its own utmsTrack cookie wins over Shopify's). */
  function budaQuery() {
    var params = new URLSearchParams();
    var discount = readCookie('discount_code');
    if (discount) params.append('discount', decodeURIComponent(discount));

    var tracking = readCookie('utmsTrack') || readCookie('_shopify_sa_p');
    if (tracking) {
      try {
        new URLSearchParams(decodeURIComponent(tracking)).forEach(function (value, key) {
          if (!params.has(key)) params.append(key, value);
        });
      } catch (error) {
        // A malformed tracking cookie costs the UTMs, never the checkout.
      }
    }
    return params.toString();
  }

  function goToBuda(base) {
    return window.fetch(shopifyRoot() + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (response) {
        if (!response.ok) throw new Error('cart ' + response.status);
        return response.json();
      })
      .then(function (cart) {
        if (!cart || !cart.token || !cart.item_count) throw new Error('empty cart');
        // The token is appended raw, "?key=" included: the Buda router reads
        // the key back out of the query, which is what the app does too.
        var url = base + '/Arrival/' + cart.token;
        var query = budaQuery();
        if (query) url += (url.indexOf('?') === -1 ? '?' : '&') + query;
        window.location.assign(url);
      });
  }

  function onCheckoutSubmit(event) {
    var form = event.target;
    if (!form || form.tagName !== 'FORM') return;
    var submitter = event.submitter;
    if (!submitter) return;

    var action = form.getAttribute('action') || '';
    var isCheckout = submitter.name === 'checkout' && /\/cart\/?$/.test(action);
    var isBuyNow = submitter.name === 'return_to' && submitter.value === '/checkout' &&
      /\/cart\/add\b/.test(action);
    if (!isCheckout && !isBuyNow) return;

    var base = budaCheckoutBase();
    if (!base || typeof window.fetch !== 'function' || typeof window.FormData !== 'function') return;

    event.preventDefault();
    event.stopPropagation();
    if (checkoutBusy) return;
    checkoutBusy = true;

    var added = isCheckout;
    var step = isCheckout
      ? Promise.resolve()
      : window.fetch(shopifyRoot() + 'cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form)
      }).then(function (response) {
        if (!response.ok) throw new Error('cart add ' + response.status);
        added = true;
      });

    step
      .then(function () { return goToBuda(base); })
      .catch(function () {
        checkoutBusy = false;
        if (added) {
          window.location.assign(shopifyRoot() + 'checkout');
          return;
        }
        // FormData and form.submit() both drop the submitter, so return_to has
        // to travel as a field of its own.
        var field = document.createElement('input');
        field.type = 'hidden';
        field.name = 'return_to';
        field.value = '/checkout';
        form.appendChild(field);
        form.submit();
      });
  }

  // Back from the Buda checkout restores this page from the bfcache with the
  // flag still set, and every checkout control would then be swallowed.
  window.addEventListener('pageshow', function () { checkoutBusy = false; });

  function initCartPanel(root) {
    if (root.getAttribute('data-vc-cart-ready') === 'true') return;
    root.setAttribute('data-vc-cart-ready', 'true');

    root.addEventListener('toggle', function (event) {
      var open = event.newState === 'open';
      Array.prototype.forEach.call(
        document.querySelectorAll('[data-vc-cart-trigger]'),
        function (link) { link.setAttribute('aria-expanded', open ? 'true' : 'false'); }
      );
      if (open) return;

      /* Escape and light dismiss both arrive here, and they arrive in different
         focus states — which is why this is not a one-line check. Traced:

           Escape         at toggle, and a whole task later, activeElement is
                          still the close button INSIDE the hidden panel;
                          it falls to <body> some time after that
           light dismiss  the pointerdown has already moved focus to <body>
           real click open  Chrome restores focus to the anchor by itself,
                          because it knows what was focused before

         An earlier guard here asked for `activeElement === document.body`. That
         is only ever true in the second case, so the Escape path was dead code
         and passed its test on Chrome's restore alone. The question to ask is
         not "did focus land on body" but "is focus still in the panel, or
         nowhere" — and if the dismissing click put focus on some other control,
         that control keeps it.

         preventScroll is the reported defect (D121), not belt and braces. The
         trigger lives in a `position: sticky` header and IS on screen, 10px
         from the top at any offset, but focus() scrolls to an element's UNSTUCK
         layout position — up at the top of the document. Closing the panel
         1800px down the page jumped it to 1342. */
      var active = document.activeElement;
      var strayed = !active || active === document.body || root.contains(active);
      if (cartInvoker && document.body.contains(cartInvoker) && strayed) {
        cartInvoker.focus({ preventScroll: true });
      }
      cartInvoker = null;
    });
  }

  /* Capture phase at the document, for the same reason the submit listener is:
     assets/custom.js sweeps `[href*="/cart"]` at DOMContentLoaded and binds a
     handler to each one that preventDefaults and calls into EvolveCore. A
     listener on the anchor would have to win a registration race with it; this
     one cannot lose, and stopPropagation keeps the event from reaching it. */
  function onCartTriggerClick(event) {
    if (!event.target.closest) return;
    var link = event.target.closest('[data-vc-cart-trigger]');
    if (!link) return;

    // Resolved at click time, not at init: the panel is a section of its own and
    // the theme editor can add or remove it underneath us. When it is not there,
    // or the browser has no popover, the href is left alone and navigates.
    if (!openCart(link)) return;

    event.preventDefault();
    event.stopPropagation();
    focusCart();
  }

  /** ARIA only. The click is handled by the delegated listener above. */
  function initCartTrigger(link) {
    if (link.getAttribute('data-vc-cart-ready') === 'true') return;
    link.setAttribute('data-vc-cart-ready', 'true');

    var root = cartRoot();
    if (!root) return;
    link.setAttribute('aria-haspopup', 'dialog');
    link.setAttribute('aria-expanded', 'false');
    if (root.id) link.setAttribute('aria-controls', root.id);
  }

  function initCart(scope) {
    var parent = scope && scope.querySelectorAll ? scope : document;
    Array.prototype.forEach.call(parent.querySelectorAll('[data-vc-cart]'), initCartPanel);
    Array.prototype.forEach.call(parent.querySelectorAll('[data-vc-cart-trigger]'), initCartTrigger);

    // The two delegated listeners are bound once for the document, not per
    // section: both survive a theme-editor reload of either section, and
    // binding them again would double every request.
    if (document.documentElement.getAttribute('data-vc-cart-bound') === 'true') return;
    document.documentElement.setAttribute('data-vc-cart-bound', 'true');
    document.addEventListener('submit', onCartFormSubmit, true);
    document.addEventListener('submit', onCheckoutSubmit, true);
    document.addEventListener('click', onCartTriggerClick, true);
    document.addEventListener('click', onCartClick);
  }

  /* ------------------------------------------------------------------------
     P06 — Explainer video
     Markup: sections/vcert-pdp-about.liquid   Styling: assets/vcert.css

     The <video> deliberately carries no `autoplay` attribute. With it the loop
     would start before this file runs, so a visitor who asked for reduced
     motion would see a flash of motion before anything could stop it. Starting
     it here instead means the poster simply stays put for them — and for anyone
     with JavaScript off, which is why the poster is not optional.

     play() returns a promise that rejects when the browser refuses autoplay
     (a policy the muted attribute usually satisfies, but not always). The
     rejection is caught and ignored: the poster is already the correct
     fallback, and an unhandled rejection would be the only visible symptom.
     ---------------------------------------------------------------------- */

  function initAboutVideo(el) {
    if (el.getAttribute('data-vc-about-ready') === 'true') return;
    el.setAttribute('data-vc-about-ready', 'true');

    if (prefersReducedMotion()) return;

    var started = el.play();
    if (started && typeof started.catch === 'function') {
      started.catch(function () {});
    }
  }

  function initAboutVideos(parent) {
    Array.prototype.forEach.call(
      parent.querySelectorAll('[data-vc-about-video]'),
      initAboutVideo
    );
  }

  /* ------------------------------------------------------------------------
     K03b — Category results, swapped in place
     Markup: sections/vcert-cat-results.liquid   (analysis/collection-e-cpf)

     Progressive enhancement over plain links (decisions.md D14, D20). The
     pagination already works without this: every page is a real URL. What
     this adds is fetching that same URL through the Section Rendering API
     (?section_id=), replacing only [data-vc-cat-region], and pushing the URL,
     so the page, the header and the scroll position below the results stay.

     - A modified click (new tab, new window, download) is the browser's.
     - Any failure falls back to ordinary navigation: the enhancement can
       break without taking the capability with it.
     - Focus moves to the region — the link that was clicked is gone.
     - The summary is announced through [data-vc-cat-status], a live region
       OUTSIDE the swapped markup: a replaced node is not reliably announced.
     - Back/forward re-render from the URL without pushing.
     - Later consumers (K06's sort, K04's filters) call swapResults() with
       their form's URL; the swap itself is theirs to share.

     K06 — the toolbar (decisions.md D21). It sits OUTSIDE the region, so:
     - a sort change passes `stay`: the select is still there, so focus stays
       on it, and the page only scrolls if the top of the results has been
       scrolled past;
     - the select is re-filled from every response, or Back would leave it
       showing the sort the grid no longer has;
     - only the newest request may land. Arrowing through a closed select
       fires `change` per key on some platforms, and responses do not arrive
       in order.
     K04 — the filters (decisions.md D22). A checkbox is one more control of
     the same form, so a change takes the same path as the sort. What differs:
     - the panel is re-rendered by every swap (its counts move), piece by
       piece: each [data-vc-cat-sync] outside the region is refilled from the
       element with the same id in the response. Focus is put back by id, and
       a group the visitor closed stays closed;
     - "Limpar filtros" is a real link; here it is the current address minus
       the filters, so whatever else is in the URL survives;
     - while the panel is a modal sheet the page behind it is inert, its live
       region included, so the announcement goes to the one inside the sheet.
     Opening and closing the sheet is the C03c drawer module's, unchanged.

     K06 search (decisions.md D25). Shopify has no collection-scoped search
     and the grid is paginated, so a filter over the cards on the page would
     miss every later page (D19). Instead:
     - sections/vcert-cat-search-index.liquid is fetched by file name, at the
       current filter/sort state, once per state: every card of the
       collection, the grid's own markup;
     - the cards whose name holds every word of the term (case and accents
       ignored) fill the region, with a count and no pagination;
     - it shares swapResults' ticket, so only the newest of the two lands;
       a filter or sort change while searching refetches and re-searches;
     - clearing the term swaps the paginated page back from the URL;
     - the term is not put in the URL: Shopify ignores it on a collection,
       and a reload shows the page the address describes.
     The sheet's "Acessar N produtos" counts the search's matches while one
     is shown.

     The column and view toggles are presentation: data-columns and data-view
     on the root (which no swap replaces) and localStorage. The section's inline script applies a
     stored choice before first paint; initCatResults repeats it for the theme
     editor, where that script does not run, and sets aria-pressed.
     ---------------------------------------------------------------------- */

  var CAT_COLUMNS_KEY = 'vcCatColumns';
  var CAT_VIEW_KEY = 'vcCatView';
  var catSwapTicket = 0;

  function catSectionUrl(url, sectionId) {
    var target = new URL(url, window.location.href);
    target.searchParams.set('section_id', sectionId);
    return target.toString();
  }

  function swapResults(root, url, push, stay) {
    var sectionId = root.getAttribute('data-vc-section-id');
    var region = root.querySelector('[data-vc-cat-region]');
    var status = root.querySelector('[data-vc-cat-status]');
    if (!sectionId || !region) {
      window.location.href = url;
      return Promise.resolve();
    }

    var ticket = (catSwapTicket += 1);
    region.setAttribute('aria-busy', 'true');
    return window.fetch(catSectionUrl(url, sectionId), { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error('section fetch ' + response.status);
        return response.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector('[data-vc-cat-region]');
        if (!fresh) throw new Error('no results region in the response');
        if (ticket !== catSwapTicket) return;

        if (push) window.history.pushState({ vcCat: true }, '', url);

        // A filter or sort change while a term is set: the controls take the
        // new state, and the region is the search's to fill, not page 1's.
        if (catSearchTerm(root)) {
          syncCatControls(root, doc);
          runCatSearch(root);
          return;
        }

        root.removeAttribute('data-vc-cat-searching');
        region.innerHTML = fresh.innerHTML;
        region.removeAttribute('aria-busy');

        var summary = region.querySelector('[data-vc-cat-summary]');
        var sheetStatus = root.querySelector('dialog[open] [data-vc-cat-sheet-status]');
        var announce = sheetStatus || status;
        if (announce) announce.textContent = summary ? summary.textContent : '';

        syncCatControls(root, doc);

        if (!stay || root.getBoundingClientRect().top < 0) {
          root.scrollIntoView({ block: 'start', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
        }
        if (!stay) region.focus({ preventScroll: true });
      })
      .catch(function () {
        if (ticket === catSwapTicket) window.location.href = url;
      });
  }

  function syncCatControls(root, doc) {
    var active = document.activeElement;
    var focusId = active && active.id && root.contains(active) ? active.id : '';
    var closed = Array.prototype.map.call(
      root.querySelectorAll('[data-vc-cat-sync] details[id]:not([open])'),
      function (group) { return group.id; }
    );

    Array.prototype.forEach.call(root.querySelectorAll('[data-vc-cat-sync]'), function (target) {
      var fresh = target.id ? doc.getElementById(target.id) : null;
      if (!fresh) return;
      target.innerHTML = fresh.innerHTML;
      // A select takes its value from its options' selected attributes, but
      // only while nobody has touched it; after that it has to be told.
      if ('value' in target && 'value' in fresh) target.value = fresh.value;
    });

    closed.forEach(function (id) {
      var group = document.getElementById(id);
      if (group) group.removeAttribute('open');
    });

    if (focusId && (!document.activeElement || document.activeElement.id !== focusId)) {
      var again = document.getElementById(focusId);
      if (again) again.focus({ preventScroll: true });
    }
  }

  // The address the results form describes: everything the form owns is taken
  // out of the current URL and put back from the form, so whatever else is
  // there (a view, a tracking parameter) stays. `keep` false drops the
  // filters instead, which is what "Limpar filtros" means.
  function catStateUrl(root, keep) {
    var target = new URL(window.location.href);
    var owned = [];
    target.searchParams.forEach(function (value, key) {
      if (key === 'page' || key.indexOf('filter.') === 0 || (keep && key === 'sort_by')) owned.push(key);
    });
    owned.forEach(function (key) { target.searchParams.delete(key); });

    var form = keep ? root.querySelector('[data-vc-cat-form]') : null;
    if (form) {
      new FormData(form).forEach(function (value, key) {
        target.searchParams.append(key, value);
      });
    }
    return target.toString();
  }

  var CAT_SEARCH_SECTION = 'vcert-cat-search-index';
  var catSearchCache = {};
  var catSearchTimer = 0;

  function catSearchInput(root) {
    return root.querySelector('[data-vc-cat-search-input]');
  }

  function catSearchTerm(root) {
    var input = catSearchInput(root);
    return input ? input.value.trim() : '';
  }

  function catNormalize(text) {
    return text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
  }

  // Every card of the collection at the current filter/sort state, fetched
  // once per state. A failed fetch is not cached, so the next keystroke retries.
  function catSearchCards(root) {
    var url = catSectionUrl(catStateUrl(root, true), CAT_SEARCH_SECTION);
    if (!catSearchCache[url]) {
      catSearchCache[url] = window.fetch(url, { credentials: 'same-origin' })
        .then(function (response) {
          if (!response.ok) throw new Error('search index ' + response.status);
          return response.text();
        })
        .then(function (html) {
          var list = new DOMParser().parseFromString(html, 'text/html')
            .querySelector('[data-vc-cat-search-index]');
          if (!list) throw new Error('no search index in the response');
          return Array.prototype.slice.call(list.children);
        })
        .catch(function (error) {
          delete catSearchCache[url];
          throw error;
        });
    }
    return catSearchCache[url];
  }

  function runCatSearch(root) {
    var region = root.querySelector('[data-vc-cat-region]');
    var form = root.querySelector('[data-vc-cat-search]');
    var term = catSearchTerm(root);
    if (!region || !form) return;

    if (!term) {
      // Back to the page the address describes, sort and filters included.
      if (root.hasAttribute('data-vc-cat-searching')) swapResults(root, window.location.href, false, true);
      return;
    }

    var ticket = (catSwapTicket += 1);
    region.setAttribute('aria-busy', 'true');
    catSearchCards(root)
      .then(function (cards) {
        if (ticket !== catSwapTicket) return;

        var words = catNormalize(term).split(/\s+/);
        var matches = cards.filter(function (card) {
          var title = card.querySelector('.vc-card__title');
          var text = catNormalize((title || card).textContent);
          return words.every(function (word) { return text.indexOf(word) !== -1; });
        });

        var summary = document.createElement(matches.length ? 'p' : 'span');
        summary.setAttribute('data-vc-cat-summary', '');
        summary.textContent = matches.length
          ? form.getAttribute('data-vc-cat-search-summary').replace('[count]', matches.length).replace('[term]', term)
          : form.getAttribute('data-vc-cat-search-empty').replace('[term]', term);

        var content;
        if (matches.length) {
          var grid = document.createElement('ul');
          grid.className = 'vc-cat-grid';
          grid.setAttribute('role', 'list');
          matches.forEach(function (card) { grid.appendChild(card.cloneNode(true)); });
          summary.className = 'vc-cat-pagination__summary';
          var footer = document.createElement('div');
          footer.className = 'vc-cat-pagination';
          footer.appendChild(summary);
          content = [grid, footer];
        } else {
          var clear = document.createElement('button');
          clear.type = 'button';
          clear.className = 'vc-cat-empty__action';
          clear.setAttribute('data-vc-cat-search-clear', '');
          clear.textContent = form.getAttribute('data-vc-cat-search-clear-label');
          var text = document.createElement('p');
          text.className = 'vc-cat-empty__text';
          text.appendChild(summary);
          text.appendChild(clear);
          var empty = document.createElement('div');
          empty.className = 'vc-cat-empty';
          empty.appendChild(text);
          content = [empty];
        }

        root.setAttribute('data-vc-cat-searching', '');
        region.textContent = '';
        content.forEach(function (node) { region.appendChild(node); });
        region.removeAttribute('aria-busy');

        var total = root.querySelector('.vc-cat-filters__apply [data-vc-cat-sync]');
        if (total) total.textContent = matches.length;

        var sheetStatus = root.querySelector('dialog[open] [data-vc-cat-sheet-status]');
        var announce = sheetStatus || root.querySelector('[data-vc-cat-status]');
        if (announce) announce.textContent = summary.textContent;

        if (root.getBoundingClientRect().top < 0) {
          root.scrollIntoView({ block: 'start', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
        }
      })
      .catch(function () {
        if (ticket !== catSwapTicket) return;
        // The enhancement broke; the store search still answers the question.
        region.removeAttribute('aria-busy');
        form.submit();
      });
  }

  function onCatSearchInput(event) {
    var input = event.target;
    if (!input.matches || !input.matches('[data-vc-cat-search-input]')) return;
    var root = input.closest('[data-vc-cat-results]');
    if (!root || root.getAttribute('data-vc-cat-ready') !== 'true') return;

    window.clearTimeout(catSearchTimer);
    catSearchTimer = window.setTimeout(function () { runCatSearch(root); }, 250);
  }

  function onCatSearchSubmit(event) {
    var form = event.target;
    if (!form.matches || !form.matches('[data-vc-cat-search]')) return;
    var root = form.closest('[data-vc-cat-results]');
    if (!root || root.getAttribute('data-vc-cat-ready') !== 'true') return;

    event.preventDefault();
    window.clearTimeout(catSearchTimer);
    runCatSearch(root);
  }

  function storedCatColumns() {
    try {
      return window.localStorage.getItem(CAT_COLUMNS_KEY) === '4' ? '4' : '3';
    } catch (e) {
      return '3';
    }
  }

  function applyCatColumns(root, value) {
    if (value === '4') root.setAttribute('data-columns', '4');
    else root.removeAttribute('data-columns');

    Array.prototype.forEach.call(root.querySelectorAll('[data-vc-cat-columns-btn]'), function (button) {
      var pressed = button.getAttribute('data-vc-cat-columns-btn') === value;
      button.setAttribute('aria-pressed', pressed ? 'true' : 'false');
    });
  }

  function storedCatView() {
    try {
      return window.localStorage.getItem(CAT_VIEW_KEY) === 'list' ? 'list' : 'grid';
    } catch (e) {
      return 'grid';
    }
  }

  function applyCatView(root, value) {
    if (value === 'list') root.setAttribute('data-view', 'list');
    else root.removeAttribute('data-view');

    Array.prototype.forEach.call(root.querySelectorAll('[data-vc-cat-view-btn]'), function (button) {
      var pressed = button.getAttribute('data-vc-cat-view-btn') === value;
      button.setAttribute('aria-pressed', pressed ? 'true' : 'false');
    });
  }

  function onCatChange(event) {
    var control = event.target;
    if (!control.matches || !control.matches('[data-vc-cat-sort], [data-vc-cat-filter]')) return;

    var root = control.closest('[data-vc-cat-results]');
    if (!root || root.getAttribute('data-vc-cat-ready') !== 'true') return;

    // The page number is dropped: page 3 of another order, or of a shorter
    // list, is not where anyone meant to be.
    swapResults(root, catStateUrl(root, true), true, true);
  }

  function onCatClick(event) {
    var columns = event.target.closest && event.target.closest('[data-vc-cat-columns-btn]');
    if (columns) {
      var value = columns.getAttribute('data-vc-cat-columns-btn') === '4' ? '4' : '3';
      applyCatColumns(columns.closest('[data-vc-cat-results]'), value);
      try {
        window.localStorage.setItem(CAT_COLUMNS_KEY, value);
      } catch (e) {
        // Storage refused (private mode, blocked site data): the choice still
        // holds for this page, it just is not remembered.
      }
      return;
    }

    var view = event.target.closest && event.target.closest('[data-vc-cat-view-btn]');
    if (view) {
      var viewValue = view.getAttribute('data-vc-cat-view-btn') === 'list' ? 'list' : 'grid';
      applyCatView(view.closest('[data-vc-cat-results]'), viewValue);
      try {
        window.localStorage.setItem(CAT_VIEW_KEY, viewValue);
      } catch (e) {
        // As for the columns: held for this page, not remembered.
      }
      return;
    }

    var clearSearch = event.target.closest && event.target.closest('[data-vc-cat-search-clear]');
    if (clearSearch) {
      var searchRoot = clearSearch.closest('[data-vc-cat-results]');
      var input = searchRoot && catSearchInput(searchRoot);
      if (!input) return;
      input.value = '';
      input.focus();
      runCatSearch(searchRoot);
      return;
    }

    var link = event.target.closest && event.target.closest('a[data-vc-cat-link], a[data-vc-cat-clear]');
    if (!link) return;
    if (event.defaultPrevented || event.button !== 0 ||
        event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    var root = link.closest('[data-vc-cat-results]');
    if (!root || root.getAttribute('data-vc-cat-ready') !== 'true') return;

    event.preventDefault();
    if (link.hasAttribute('data-vc-cat-clear')) {
      // In the panel the link survives the swap and keeps focus; in the empty
      // state it is replaced along with the region, so focus goes there.
      var region = root.querySelector('[data-vc-cat-region]');
      swapResults(root, catStateUrl(root, false), true, !(region && region.contains(link)));
      return;
    }
    swapResults(root, link.href, true);
  }

  function onCatPopState() {
    var root = document.querySelector('[data-vc-cat-results][data-vc-cat-ready="true"]');
    if (root) swapResults(root, window.location.href, false);
  }

  function initCatResults(scope) {
    Array.prototype.forEach.call(scope.querySelectorAll('[data-vc-cat-results]'), function (root) {
      root.setAttribute('data-vc-cat-ready', 'true');
      root.setAttribute('data-vc-cat-js', '');
      applyCatColumns(root, storedCatColumns());
      applyCatView(root, storedCatView());
    });

    // Delegated, so a region swapped in later needs nothing re-bound. Safe to
    // run again on shopify:section:load: addEventListener ignores a listener
    // already registered with the same function reference, and onCatClick
    // also steps aside for a click another handler has already taken — each
    // alone keeps one click to one fetch (both removed: three fetches, measured).
    document.addEventListener('click', onCatClick);
    document.addEventListener('change', onCatChange);
    document.addEventListener('input', onCatSearchInput);
    document.addEventListener('submit', onCatSearchSubmit);
    window.addEventListener('popstate', onCatPopState);
  }

  /* ------------------------------------------------------------------------
     K12 — Category "about" block
     Markup: sections/vcert-cat-about.liquid   (analysis/collection-e-cpf)

     Marking the section applies the three-line clamp (CSS); the button is
     revealed only if the clamp actually hides something, which depends on
     the copy and the width. The text stays in the page either way: this is
     a clamp, not a truncation, so it is selectable and indexed.
     ---------------------------------------------------------------------- */

  function initCatAbout(section) {
    if (section.getAttribute('data-vc-cat-about-ready') === 'true') return;

    var content = section.querySelector('[data-vc-cat-about-content]');
    var toggle = section.querySelector('[data-vc-cat-about-toggle]');
    if (!content || !toggle) return;

    section.setAttribute('data-vc-cat-about-ready', 'true');
    if (content.scrollHeight - content.clientHeight <= 1) return;

    toggle.hidden = false;
    toggle.addEventListener('click', function () {
      var open = toggle.getAttribute('aria-expanded') !== 'true';
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      content.classList.toggle('is-expanded', open);
      toggle.textContent = toggle.getAttribute(open ? 'data-label-less' : 'data-label-more');
    });
  }

  function initCatAbouts(scope) {
    var parent = scope && scope.querySelectorAll ? scope : document;
    Array.prototype.forEach.call(parent.querySelectorAll('[data-vc-cat-about]'), initCatAbout);
  }

  /* ------------------------------------------------------------------------
     B05 — Search results: "Carregar mais" (analysis/search)
     Markup: sections/vcert-search.liquid   Styling: assets/vcert.css

     Progressive reveal over a list the server already rendered in full: no
     request, and without this script every hit stays visible. A group opts in
     with [data-vc-search-more] on its root and [data-vc-search-more-list] on
     its <ul>, and declares how it reveals:

       data-vc-search-more="rows"   first N rows of the grid (data-vc-search-
                                    more-rows, default 2), then one row per
                                    click. The column count is read from the
                                    grid's computed tracks, as the reference
                                    does, so a "row" follows the viewport.
       data-vc-search-more="count"  first N items (data-vc-search-more-count,
                                    default 6), then everything.

     The first slice is already hidden by CSS before this runs (html.js), so
     the page never paints every hit and then shrinks. Once ready, [hidden] on
     each item takes over from that rule.

     Unlike the reference, focus moves to the first newly shown item's link:
     otherwise a keyboard or screen-reader user stays on the button with no
     sign anything appeared — and on the last click the button is removed,
     taking focus with it.
     ---------------------------------------------------------------------- */

  function gridColumns(list) {
    var tracks = window.getComputedStyle(list).gridTemplateColumns;
    var count = tracks && tracks !== 'none' ? tracks.split(' ').filter(Boolean).length : 1;
    return count || 1;
  }

  function initSearchMore(group) {
    if (group.getAttribute('data-vc-search-more-ready') === 'true') return;
    var list = group.querySelector('[data-vc-search-more-list]');
    if (!list) return;

    var items = Array.prototype.slice.call(list.children);
    var byRows = group.getAttribute('data-vc-search-more') === 'rows';
    var rows = parseInt(group.getAttribute('data-vc-search-more-rows'), 10) || 2;
    var shown = byRows
      ? gridColumns(list) * rows
      : parseInt(group.getAttribute('data-vc-search-more-count'), 10) || 6;

    group.setAttribute('data-vc-search-more-ready', 'true');
    if (items.length <= shown) return;

    items.forEach(function (item, index) { item.hidden = index >= shown; });

    var wrap = document.createElement('div');
    wrap.className = 'vc-search__more-wrap';
    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'vc-btn vc-btn--secondary vc-search__more';
    button.textContent = group.getAttribute('data-vc-search-more-label') || 'Carregar mais';
    wrap.appendChild(button);
    list.parentNode.insertBefore(wrap, list.nextSibling);

    button.addEventListener('click', function () {
      var first = shown;
      shown = byRows ? Math.min(items.length, shown + gridColumns(list)) : items.length;
      items.forEach(function (item, index) { item.hidden = index >= shown; });
      if (shown >= items.length) wrap.parentNode.removeChild(wrap);

      var target = items[first] && items[first].querySelector('a[href]');
      if (target) target.focus();
    });
  }

  function initSearchMores(scope) {
    var parent = scope && scope.querySelectorAll ? scope : document;
    Array.prototype.forEach.call(parent.querySelectorAll('[data-vc-search-more]'), initSearchMore);
  }

  /* ------------------------------------------------------------------------
     S01 — Help-center bar: stuck state  (analysis/page-faq)
     Markup: snippets/vcert-faq-bar.liquid   Styling: assets/vcert.css

     The bar is position: sticky in CSS and sticks without this script. All
     this adds is .is-stuck (white, compact) while the 1px sentinel above it
     has scrolled under the header. The observer's top margin is the bar's
     own computed `top`, so it follows the header height without knowing it.
     ---------------------------------------------------------------------- */

  function initFaqBar(bar) {
    if (bar.getAttribute('data-vc-faq-bar-ready') === 'true') return;
    var sentinel = bar.previousElementSibling;
    if (!sentinel || !sentinel.hasAttribute('data-vc-faq-bar-sentinel')) return;
    if (typeof window.IntersectionObserver !== 'function') return;
    bar.setAttribute('data-vc-faq-bar-ready', 'true');

    var top = parseFloat(window.getComputedStyle(bar).top) || 0;
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var above = entry.boundingClientRect.top < top + 1;
        bar.classList.toggle('is-stuck', !entry.isIntersecting && above);
      });
    }, { rootMargin: '-' + Math.round(top) + 'px 0px 0px 0px', threshold: 0 });
    observer.observe(sentinel);
  }

  function initFaqBars(scope) {
    var parent = scope && scope.querySelectorAll ? scope : document;
    Array.prototype.forEach.call(parent.querySelectorAll('[data-vc-faq-bar]'), initFaqBar);
  }

  /* ------------------------------------------------------------------------
     S02 — Help-center search  (analysis/page-faq D4)
     Markup: snippets/vcert-faq-bar.liquid   Styling: assets/vcert.css

     The form submits to /search without this script. This adds suggestions:
     sections/vcert-faq-search-index.liquid (every published article of the
     blog, title + start of the answer) is fetched once, on first focus or
     keystroke, and filtered locally — every word of the term, case and
     accents ignored (catNormalize, K06). Two characters minimum; at most
     data-limit results; an empty state naming the term; "see all" to
     /search?q= when there are more. The count goes to a polite live region.
     Escape clears and closes; a click outside closes.

     Also the repository's search (analysis/page-repositorio R02, D9): the
     bar names its own index section (data-index-section), an entry may carry
     data-meta (rendered as a second line — the category), and data-no-submit
     keeps Enter on the page, since /search does not list the repository's
     pages. A bar without those attributes renders exactly as before.
     ---------------------------------------------------------------------- */

  var FAQ_SEARCH_SECTION = 'vcert-faq-search-index';
  var FAQ_SEARCH_MIN = 2;
  var faqSearchCache = {};

  function faqSearchIndex(url, section) {
    var target = catSectionUrl(url, section || FAQ_SEARCH_SECTION);
    if (!faqSearchCache[target]) {
      faqSearchCache[target] = window.fetch(target, { credentials: 'same-origin' })
        .then(function (response) {
          if (!response.ok) throw new Error('faq search index ' + response.status);
          return response.text();
        })
        .then(function (html) {
          var list = new DOMParser().parseFromString(html, 'text/html')
            .querySelector('[data-vc-faq-search-index]');
          if (!list) throw new Error('no faq search index in the response');
          return Array.prototype.map.call(list.querySelectorAll('a[href]'), function (a) {
            return {
              href: a.getAttribute('href'),
              title: a.textContent.trim(),
              meta: a.getAttribute('data-meta') || '',
              match: catNormalize(a.getAttribute('data-match') || a.textContent)
            };
          });
        })
        .catch(function (error) {
          delete faqSearchCache[target];
          throw error;
        });
    }
    return faqSearchCache[target];
  }

  function initFaqSearch(root) {
    if (root.getAttribute('data-vc-faq-search-ready') === 'true') return;
    var input = root.querySelector('[data-vc-faq-search-input]');
    var clear = root.querySelector('[data-vc-faq-search-clear]');
    var panel = root.querySelector('[data-vc-faq-search-results]');
    var list = root.querySelector('[data-vc-faq-search-list]');
    var empty = root.querySelector('[data-vc-faq-search-empty]');
    var more = root.querySelector('[data-vc-faq-search-more]');
    var status = root.querySelector('[data-vc-faq-search-status]');
    if (!input || !panel || !list) return;
    root.setAttribute('data-vc-faq-search-ready', 'true');

    var indexUrl = root.getAttribute('data-index-url') || window.location.pathname;
    var indexSection = root.getAttribute('data-index-section') || FAQ_SEARCH_SECTION;
    var searchUrl = root.getAttribute('data-search-url') || '/search';
    var limit = parseInt(root.getAttribute('data-limit'), 10) || 3;
    var ticket = 0;

    function close() {
      panel.hidden = true;
    }

    function announce(text) {
      if (status) status.textContent = text;
    }

    function render(term, items) {
      list.textContent = '';
      items.slice(0, limit).forEach(function (item) {
        var li = document.createElement('li');
        var a = document.createElement('a');
        a.className = 'vc-faq-search__result';
        a.href = item.href;
        if (item.meta) {
          var label = document.createElement('span');
          label.className = 'vc-faq-search__result-label';
          label.textContent = item.title;
          var meta = document.createElement('span');
          meta.className = 'vc-faq-search__meta';
          meta.textContent = item.meta;
          a.appendChild(label);
          a.appendChild(meta);
        } else {
          a.textContent = item.title;
        }
        li.appendChild(a);
        list.appendChild(li);
      });

      if (empty) {
        empty.hidden = items.length > 0;
        empty.textContent = items.length ? '' : (root.getAttribute('data-empty') || '').replace('[term]', term);
      }
      if (more) {
        more.hidden = items.length <= limit;
        more.href = searchUrl + '?q=' + encodeURIComponent(term);
      }
      panel.hidden = false;

      if (!items.length) {
        announce(empty ? empty.textContent : '');
      } else {
        announce(items.length === 1
          ? root.getAttribute('data-count-one')
          : (root.getAttribute('data-count-other') || '').replace('[count]', items.length));
      }
    }

    function run() {
      var term = input.value.trim();
      if (term.length < FAQ_SEARCH_MIN) {
        ticket += 1;
        close();
        announce('');
        return;
      }
      var mine = (ticket += 1);
      var words = catNormalize(term).split(/\s+/).filter(Boolean);
      faqSearchIndex(indexUrl, indexSection)
        .then(function (entries) {
          if (mine !== ticket) return;
          render(term, entries.filter(function (entry) {
            return words.every(function (word) { return entry.match.indexOf(word) !== -1; });
          }));
        })
        .catch(function () {
          if (mine !== ticket) return;
          list.textContent = '';
          if (more) more.hidden = true;
          if (empty) {
            empty.hidden = false;
            empty.textContent = root.getAttribute('data-error') || '';
          }
          panel.hidden = false;
          announce(empty ? empty.textContent : '');
        });
    }

    var debounced = debounce(run, 150);

    if (root.hasAttribute('data-no-submit')) {
      var form = root.querySelector('form');
      if (form) {
        form.addEventListener('submit', function (event) {
          event.preventDefault();
          run();
        });
      }
    }

    input.addEventListener('focus', function () {
      // Warm the index before the first keystroke; a failure retries later.
      faqSearchIndex(indexUrl, indexSection).catch(function () {});
      if (input.value.trim().length >= FAQ_SEARCH_MIN) run();
    });
    input.addEventListener('input', debounced);

    root.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      if (panel.hidden && !input.value) return;
      input.value = '';
      ticket += 1;
      close();
      announce('');
      input.focus();
    });

    if (clear) {
      clear.addEventListener('click', function () {
        input.value = '';
        ticket += 1;
        close();
        announce('');
        input.focus();
      });
    }

    document.addEventListener('click', function (event) {
      if (!root.contains(event.target)) close();
    });
    root.addEventListener('focusout', function (event) {
      if (event.relatedTarget && !root.contains(event.relatedTarget)) close();
    });
  }

  function initFaqSearches(scope) {
    var parent = scope && scope.querySelectorAll ? scope : document;
    Array.prototype.forEach.call(parent.querySelectorAll('[data-vc-faq-search]'), initFaqSearch);
  }

  /* ------------------------------------------------------------------------
     S05 — "Iniciar atendimento online" opens the chat  (analysis/page-faq D3)
     Markup: sections/vcert-faq-hub-contact.liquid

     Tidio's documented API: window.tidioChatApi, ready once 'tidioChat-ready'
     has fired (or .on('ready') when the API object already exists). Until
     then a fallback link is simply followed, and a fallback-less button stays
     hidden. Nothing here knows the chat's script URL or loads it.

     data-vc-chat-hide-launcher (D6): on a page whose opener carries it, the
     floating launcher is hidden once the chat is ready, and hidden again
     every time the visitor closes the window — the button is the only way
     in. Tidio's own recipe ("Opening the Chat Widget from a custom button").
     Before 'ready' the section's inline style keeps the launcher invisible;
     data-vc-chat-managed on <html> releases that style once hide() owns it,
     so Tidio's own show() is not fighting a CSS rule.
     ---------------------------------------------------------------------- */

  var chatReady = false;
  var chatWaiters = [];
  var chatLauncherManaged = false;

  function onChatReady(fn) {
    if (chatReady) { fn(); return; }
    chatWaiters.push(fn);
    if (chatWaiters.length > 1) return;
    function ready() {
      if (chatReady) return;
      chatReady = true;
      chatWaiters.splice(0).forEach(function (cb) { cb(); });
    }
    if (window.tidioChatApi && typeof window.tidioChatApi.on === 'function') {
      window.tidioChatApi.on('ready', ready);
    }
    document.addEventListener('tidioChat-ready', ready);
  }

  function manageChatLauncher() {
    if (chatLauncherManaged) return;
    var api = window.tidioChatApi;
    if (!api || typeof api.hide !== 'function') return;
    chatLauncherManaged = true;
    api.hide();
    if (typeof api.on === 'function') {
      api.on('close', function () { api.hide(); });
    }
    document.documentElement.setAttribute('data-vc-chat-managed', '');
  }

  function initChatOpener(el) {
    if (el.getAttribute('data-vc-chat-ready') === 'true') return;
    el.setAttribute('data-vc-chat-ready', 'true');
    var hideLauncher = el.hasAttribute('data-vc-chat-hide-launcher');

    onChatReady(function () {
      if (hideLauncher) manageChatLauncher();
      el.hidden = false;
    });

    el.addEventListener('click', function (event) {
      var api = window.tidioChatApi;
      if (!chatReady || !api || typeof api.open !== 'function') return;
      event.preventDefault();
      if (typeof api.show === 'function') api.show();
      api.open();
    });
  }

  function initChatOpeners(scope) {
    var parent = scope && scope.querySelectorAll ? scope : document;
    Array.prototype.forEach.call(parent.querySelectorAll('[data-vc-chat-open]'), initChatOpener);
  }

  /* ------------------------------------------------------------------------
     F05 — Lateral categories drawer  (analysis/page-faq-article D3; the
           repository hub's R05, analysis/page-repositorio D11)
     Markup: sections/vcert-faq-article.liquid   Styling: assets/vcert.css

     The category <nav> is rendered once, in the sticky column F04 shows from
     64rem. Below that the column is hidden, and this module moves the same
     <nav> into the (empty) <dialog> when it opens and back when it closes —
     so the page carries one copy of the links and one landmark, not the
     reference's two. Everything else is the native <dialog>, as in C03c:
     focus trap, Escape, ::backdrop, inert page. On top of it: backdrop click
     and the close button close, aria-expanded mirrors state, focus returns
     to the opener, html.is-locked as in C03c, and crossing into 64rem with
     the drawer open closes it so the column gets its <nav> back.

     The button ships `hidden`: it is revealed here, once it can work.
     ---------------------------------------------------------------------- */

  function initFaqNavDrawer(button) {
    if (button.getAttribute('data-vc-faq-nav-ready') === 'true') return;

    var drawer = document.getElementById(button.getAttribute('aria-controls') || '');
    var body = drawer && drawer.querySelector('[data-vc-faq-drawer-body]');
    // The lateral layout's root (D5, analysis/page-repositorio R05): the FAQ
    // article's and the repository's both carry it.
    var root = button.closest('[data-vc-lateral]');
    var home = root && root.querySelector('[data-vc-faq-nav-home]');
    var nav = home && home.querySelector('[data-vc-faq-nav]');
    if (!body || !nav || typeof drawer.showModal !== 'function') return;

    button.setAttribute('data-vc-faq-nav-ready', 'true');
    button.hidden = false;

    button.addEventListener('click', function () {
      if (drawer.open) return;
      body.appendChild(nav);
      drawer.showModal();
      button.setAttribute('aria-expanded', 'true');
      setDrawerLock(true);
    });

    Array.prototype.forEach.call(
      drawer.querySelectorAll('[data-vc-faq-drawer-close]'),
      function (closer) {
        closer.addEventListener('click', function () { drawer.close(); });
      }
    );

    // A click on the <dialog> itself is a click on the backdrop (C03c).
    drawer.addEventListener('click', function (event) {
      if (event.target === drawer) drawer.close();
    });

    // Every route out — Escape, the button, the backdrop, the breakpoint.
    drawer.addEventListener('close', function () {
      home.appendChild(nav);
      button.setAttribute('aria-expanded', 'false');
      setDrawerLock(false);
      if (window.getComputedStyle(button).display !== 'none') button.focus();
    });

    if (typeof window.matchMedia === 'function') {
      var wide = window.matchMedia('(min-width: 64rem)');
      var onChange = function () { if (wide.matches && drawer.open) drawer.close(); };
      if (typeof wide.addEventListener === 'function') wide.addEventListener('change', onChange);
      else if (typeof wide.addListener === 'function') wide.addListener(onChange);
    }
  }

  function initFaqNavDrawers(scope) {
    var parent = scope && scope.querySelectorAll ? scope : document;
    Array.prototype.forEach.call(parent.querySelectorAll('[data-vc-faq-nav-open]'), initFaqNavDrawer);
  }

  /* ------------------------------------------------------------------------
     C18 — Consent gate  (analysis/home D127)
     Markup: snippets/vcert-consent-gate.liquid   Styling: assets/vcert.css

     The store's consent banner is a third-party <div>, not a <dialog>, so the
     modal behaviour showModal() would give for free is supplied here: while
     one of the dialogs named by data-vc-consent-gate is on screen, every other
     child of <body> is inert and html.vc-consent-open stops the scroll. The
     visitor cannot use the page until they accept, reject or save.

     1. FAIL-OPEN. Nothing is inerted until a dialog is visible, and nothing at
        all happens in an engine without `inert`. A blocked vendor script means
        no banner and no gate — never a locked store.
     2. ONLY WHAT THIS MODULE INERTED IS RELEASED. It marks its nodes with
        data-vc-consent-inert; anything made inert by someone else is left
        alone, before and after.
     3. WATCHED, NOT POLLED. The vendor injects the banner about 1.5s in and
        shows/hides it with inline style and class changes. A childList
        observer on <body> finds it, then an attribute observer on its
        body-level ancestor follows it, and the <body> observer narrows to
        direct children — which is all it still needs, to inert widgets that
        arrive while the gate is held.
     4. NO VENDOR NAMES. Selectors and the title come from the markup, like
        C14's data-vc-btt-clear.
     ---------------------------------------------------------------------- */

  var CONSENT_OPEN_CLASS = 'vc-consent-open';
  var CONSENT_TITLE_ID = 'vc-consent-title';
  // Lifts the dialogs' body-level ancestor above every other fixed layer (a
  // chat widget sits at z-index 999999999); see C18 in vcert.css.
  var CONSENT_ROOT_CLASS = 'vc-consent-root';

  function consentVisible(node) {
    return node.getClientRects().length > 0 &&
      window.getComputedStyle(node).visibility !== 'hidden';
  }

  function consentBodyChild(node) {
    while (node && node.parentElement !== document.body) node = node.parentElement;
    return node;
  }

  function initConsentGate(config) {
    if (config.getAttribute('data-vc-consent-ready') === 'true') return;
    config.setAttribute('data-vc-consent-ready', 'true');

    var selector = config.getAttribute('data-vc-consent-gate');
    if (!selector || !('inert' in HTMLElement.prototype) ||
        typeof window.MutationObserver !== 'function') return;

    // The title goes into the first dialog listed (the banner); the others
    // bring their own heading.
    var titleSelector = selector.split(',')[0].trim();
    var title = config.getAttribute('data-vc-consent-title');

    var roots = [];
    var held = false;
    var queued = false;
    var narrowed = false;

    function dialogs() {
      try {
        return Array.prototype.slice.call(document.querySelectorAll(selector));
      } catch (error) {
        return [];
      }
    }

    function label(dialog) {
      if (!title || !dialog.matches(titleSelector) || dialog.querySelector('#' + CONSENT_TITLE_ID)) return;
      var heading = document.createElement('h2');
      heading.id = CONSENT_TITLE_ID;
      heading.className = 'vc-consent__title';
      heading.textContent = title;
      dialog.insertBefore(heading, dialog.firstChild);
      dialog.setAttribute('aria-labelledby', CONSENT_TITLE_ID);
    }

    function hold(open) {
      var keep = open.map(consentBodyChild);

      Array.prototype.forEach.call(document.body.children, function (child) {
        if (keep.indexOf(child) !== -1) {
          child.classList.add(CONSENT_ROOT_CLASS);
          if (child.hasAttribute('data-vc-consent-inert')) {
            child.inert = false;
            child.removeAttribute('data-vc-consent-inert');
          }
          return;
        }
        if (child.inert || /^(SCRIPT|STYLE|LINK|TEMPLATE|NOSCRIPT)$/.test(child.tagName)) return;
        child.inert = true;
        child.setAttribute('data-vc-consent-inert', '');
      });

      document.documentElement.classList.add(CONSENT_OPEN_CLASS);
      held = true;

      open.forEach(function (dialog) {
        label(dialog);
        if (dialog.getAttribute('role') === 'dialog') dialog.setAttribute('aria-modal', 'true');
      });

      var focusInside = open.some(function (dialog) {
        return dialog.contains(document.activeElement);
      });
      if (!focusInside) {
        if (!open[0].hasAttribute('tabindex')) open[0].setAttribute('tabindex', '-1');
        open[0].focus({ preventScroll: true });
      }
    }

    function release() {
      Array.prototype.forEach.call(document.querySelectorAll('[data-vc-consent-inert]'), function (node) {
        node.inert = false;
        node.removeAttribute('data-vc-consent-inert');
      });
      Array.prototype.forEach.call(document.querySelectorAll('.' + CONSENT_ROOT_CLASS), function (node) {
        node.classList.remove(CONSENT_ROOT_CLASS);
      });
      document.documentElement.classList.remove(CONSENT_OPEN_CLASS);
      held = false;
    }

    var rootObserver = new window.MutationObserver(schedule);
    var bodyObserver = new window.MutationObserver(schedule);

    function check() {
      queued = false;
      var nodes = dialogs();

      nodes.forEach(function (node) {
        var root = consentBodyChild(node);
        if (!root || roots.indexOf(root) !== -1) return;
        roots.push(root);
        rootObserver.observe(root, {
          attributes: true,
          attributeFilter: ['style', 'class'],
          childList: true,
          subtree: true
        });
      });

      if (roots.length && !narrowed) {
        narrowed = true;
        bodyObserver.disconnect();
        bodyObserver.observe(document.body, { childList: true });
      }

      var open = nodes.filter(consentVisible);
      if (open.length) hold(open);
      else if (held) release();
    }

    function schedule() {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(check);
    }

    bodyObserver.observe(document.body, { childList: true, subtree: true });
    check();
  }

  function initConsentGates(scope) {
    var parent = scope && scope.querySelectorAll ? scope : document;
    Array.prototype.forEach.call(parent.querySelectorAll('[data-vc-consent-gate]'), initConsentGate);
  }

  /* ------------------------------------------------------------------------
     G01 — Blog featured banner (analysis/blog-certificado-digital)
     Markup: sections/vcert-blog-listing.liquid   Styling: assets/vcert.css
     Up to three slides, one active. Advances every --vc-bbanner-ms (7s) on a
     timer that keeps its remaining time across pauses; the progress bar is
     a CSS animation paused by the same attribute, so the two stay in step.
     Pauses while: the user has paused it, the pointer is over it, keyboard
     focus is inside it, the tab is hidden, or it is mostly off-screen.
     prefers-reduced-motion starts it paused (WCAG 2.2.2 is met either way by
     the pause button). Inactive slides are inert. The slides' live region is
     polite only while paused, so a manual change is announced and an
     automatic one is not. Without this script: the first slide, no controls.
     ---------------------------------------------------------------------- */

  var BBANNER_LEAVE_MS = 250;
  var BBANNER_VISIBLE_RATIO = 0.25;

  function bbannerDuration(root) {
    var raw = window.getComputedStyle(root).getPropertyValue('--vc-bbanner-ms').trim();
    var value = parseFloat(raw);
    if (!value) return 7000;
    return /ms$/.test(raw) ? value : value * 1000;
  }

  function initBlogBanner(root) {
    if (root.getAttribute('data-vc-bbanner-ready') === 'true') return;
    var slides = Array.prototype.slice.call(root.querySelectorAll('[data-vc-bbanner-slide]'));
    var media = Array.prototype.slice.call(root.querySelectorAll('[data-vc-bbanner-media]'));
    var bars = Array.prototype.slice.call(root.querySelectorAll('[data-vc-bbanner-bar]'));
    var controls = root.querySelector('[data-vc-bbanner-controls]');
    var toggle = root.querySelector('[data-vc-bbanner-toggle]');
    var prev = root.querySelector('[data-vc-bbanner-prev]');
    var next = root.querySelector('[data-vc-bbanner-next]');
    var live = root.querySelector('[data-vc-bbanner-slides]');
    if (slides.length < 2 || !controls || !toggle || !prev || !next) return;
    root.setAttribute('data-vc-bbanner-ready', 'true');
    controls.hidden = false;

    var duration = bbannerDuration(root);
    var index = 0;
    var timer = null;
    var startedAt = 0;
    var remaining = duration;
    var userPaused = prefersReducedMotion();
    var hovered = false;
    var focused = false;
    var tabHidden = document.hidden === true;
    var offscreen = false;
    var observer = null;

    function paused() {
      return userPaused || hovered || focused || tabHidden || offscreen;
    }

    function teardown() {
      window.clearTimeout(timer);
      timer = null;
      document.removeEventListener('visibilitychange', onVisibility);
      if (observer) observer.disconnect();
    }

    function schedule() {
      window.clearTimeout(timer);
      timer = null;
      // A section reloaded in the theme editor leaves this banner detached.
      if (!root.isConnected) { teardown(); return; }
      if (paused()) {
        root.setAttribute('data-vc-bbanner-paused', '');
        return;
      }
      root.removeAttribute('data-vc-bbanner-paused');
      startedAt = Date.now();
      timer = window.setTimeout(function () { go(index + 1); }, remaining);
    }

    function update() {
      if (timer) {
        remaining = Math.max(0, remaining - (Date.now() - startedAt));
      }
      schedule();
      if (live) live.setAttribute('aria-live', paused() ? 'polite' : 'off');
      toggle.setAttribute('aria-label', userPaused
        ? toggle.getAttribute('data-label-play')
        : toggle.getAttribute('data-label-pause'));
      var pauseIcon = toggle.querySelector('[data-vc-bbanner-icon-pause]');
      var playIcon = toggle.querySelector('[data-vc-bbanner-icon-play]');
      if (pauseIcon) pauseIcon.hidden = userPaused;
      if (playIcon) playIcon.hidden = !userPaused;
    }

    function go(target) {
      var count = slides.length;
      target = ((target % count) + count) % count;
      if (target === index) return;
      var leaving = slides[index];
      leaving.setAttribute('data-state', 'leaving');
      leaving.setAttribute('inert', '');
      window.setTimeout(function () {
        if (leaving.getAttribute('data-state') === 'leaving') leaving.setAttribute('data-state', 'inactive');
      }, BBANNER_LEAVE_MS);

      slides[target].removeAttribute('inert');
      slides[target].setAttribute('data-state', 'active');
      media.forEach(function (el, i) { el.setAttribute('data-state', i === target ? 'active' : 'inactive'); });
      bars.forEach(function (el, i) {
        el.setAttribute('data-state', i < target ? 'done' : (i === target ? 'active' : 'todo'));
      });
      index = target;
      remaining = duration;
      schedule();
    }

    function onVisibility() {
      tabHidden = document.hidden === true;
      update();
    }

    toggle.addEventListener('click', function () {
      userPaused = !userPaused;
      update();
    });
    prev.addEventListener('click', function () { go(index - 1); });
    next.addEventListener('click', function () { go(index + 1); });

    root.addEventListener('mouseenter', function () { hovered = true; update(); });
    root.addEventListener('mouseleave', function () { hovered = false; update(); });
    root.addEventListener('focusin', function (event) {
      var target = event.target;
      focused = !!(target && target.matches && target.matches(':focus-visible'));
      update();
    });
    root.addEventListener('focusout', function (event) {
      if (event.relatedTarget && root.contains(event.relatedTarget)) return;
      focused = false;
      update();
    });
    document.addEventListener('visibilitychange', onVisibility);

    if (typeof window.IntersectionObserver === 'function') {
      observer = new window.IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          offscreen = entry.intersectionRatio < BBANNER_VISIBLE_RATIO;
        });
        update();
      }, { threshold: [0, BBANNER_VISIBLE_RATIO, 1] });
      observer.observe(root);
    }

    update();
  }

  function initBlogBanners(scope) {
    var parent = scope && scope.querySelectorAll ? scope : document;
    Array.prototype.forEach.call(parent.querySelectorAll('[data-vc-bbanner]'), initBlogBanner);
  }

  /* ------------------------------------------------------------------------
     G05 — Blog search (analysis/blog-certificado-digital D11)
     Markup: sections/vcert-blog-listing.liquid (form, status, regions) and
     sections/vcert-blog-search-results.liquid (the cards)
     From two characters, 250ms after the last keystroke, asks the store's
     search for "<term>*" (word prefixes) through the Section Rendering API
     and shows this blog's matching cards in
     place of the list and the pagination. Clearing — the × button, Escape, or
     emptying the field — puts the list back. Enter searches without leaving
     the page. If the request fails, the form submits to /search as it does
     without JavaScript. The badge each card needs is set here from the tema
     navigation, whose links follow the Tema blocks' order (D2, D11).
     A slower answer to an older term is dropped when it lands (lastTerm),
     not aborted: Shopify's analytics wrap window.fetch and turn an aborted
     request into an uncaught rejection (collection-e-cpf, 2026-09-30 notes).
     ---------------------------------------------------------------------- */

  var BLOG_SEARCH_MIN = 2;
  var BLOG_SEARCH_DEBOUNCE = 250;
  var BLOG_SEARCH_SECTION = 'vcert-blog-search-results';

  function initBlogSearch(form) {
    if (form.getAttribute('data-vc-bloglist-search-ready') === 'true') return;
    var root = form.closest('.vc-bloglist');
    var input = form.querySelector('[data-vc-bloglist-search-input]');
    var clear = form.querySelector('[data-vc-bloglist-search-clear]');
    var status = root && root.querySelector('[data-vc-bloglist-search-status]');
    var list = root && root.querySelector('[data-vc-bloglist-list]');
    var region = root && root.querySelector('[data-vc-bloglist-search-results]');
    var emptyTemplate = root && root.querySelector('template[data-vc-bloglist-search-empty]');
    if (!root || !input || !clear || !status || !list || !region || typeof window.fetch !== 'function') return;
    form.setAttribute('data-vc-bloglist-search-ready', 'true');

    // Tema handle -> label, in navigation (= block) order.
    var temas = [];
    Array.prototype.forEach.call(root.querySelectorAll('.vc-bloglist__tabs a[href*="/tagged/"]'), function (a) {
      var handle = a.getAttribute('href').split('/tagged/')[1];
      if (handle) temas.push({ handle: decodeURIComponent(handle.split(/[?#]/)[0]), label: a.textContent.trim() });
    });

    var timer = null;
    var lastTerm = '';

    function say(key, count, term) {
      var text = status.getAttribute('data-text-' + key) || '';
      status.textContent = text.replace('[count]', String(count)).replace('[q]', term);
    }

    function restore() {
      window.clearTimeout(timer);
      lastTerm = '';
      region.hidden = true;
      region.innerHTML = '';
      list.hidden = false;
      status.textContent = '';
      clear.hidden = input.value === '';
    }

    function setBadges(scope) {
      Array.prototype.forEach.call(scope.querySelectorAll('[data-vc-tags]'), function (card) {
        var tags = card.getAttribute('data-vc-tags');
        var media = card.querySelector('.vc-blog__media');
        if (!media || media.querySelector('.vc-blog__badge')) return;
        for (var i = 0; i < temas.length; i++) {
          if (tags.indexOf('|' + temas[i].handle + '|') !== -1) {
            var badge = document.createElement('span');
            badge.className = 'vc-blog__badge';
            badge.textContent = temas[i].label;
            media.appendChild(badge);
            return;
          }
        }
      });
    }

    function run(term) {
      if (term === lastTerm) return;
      lastTerm = term;
      var url = form.getAttribute('action') + '?type=article&section_id=' + BLOG_SEARCH_SECTION +
        '&q=' + encodeURIComponent(term + '*');
      window.fetch(url, { credentials: 'same-origin' })
        .then(function (response) {
          if (!response.ok) throw new Error('HTTP ' + response.status);
          return response.text();
        })
        .then(function (html) {
          if (term !== lastTerm) return;
          var doc = new window.DOMParser().parseFromString(html, 'text/html');
          var results = doc.querySelector('[data-vc-blog-search-results]');
          var countEl = doc.querySelector('[data-vc-blog-search-count]');
          var count = countEl ? parseInt(countEl.getAttribute('data-vc-blog-search-count'), 10) || 0 : 0;
          var shown = countEl ? parseInt(countEl.getAttribute('data-vc-blog-search-shown'), 10) || 0 : 0;
          var grid = results && results.querySelector('.vc-bloglist__grid');
          region.innerHTML = '';
          if (count > 0 && grid) {
            region.appendChild(document.importNode(grid, true));
            setBadges(region);
            say(count === 1 ? 'one' : 'other', count, term);
            if (count > shown) {
              // More than the endpoint renders: the search page has them all.
              var more = document.createElement('p');
              more.className = 'vc-bloglist__search-more';
              var link = document.createElement('a');
              link.href = form.getAttribute('action') + '?type=article&q=' + encodeURIComponent(term);
              link.textContent = (status.getAttribute('data-text-more') || '').replace('[count]', String(count));
              more.appendChild(link);
              region.appendChild(more);
            }
          } else {
            if (emptyTemplate) region.appendChild(document.importNode(emptyTemplate.content, true));
            say('none', 0, term);
          }
          list.hidden = true;
          region.hidden = false;
        })
        .catch(function () {
          if (term !== lastTerm) return;
          // The search page is the same answer, without the in-page swap.
          form.submit();
        });
    }

    input.addEventListener('input', function () {
      clear.hidden = input.value === '';
      window.clearTimeout(timer);
      var term = input.value.trim();
      if (term.length < BLOG_SEARCH_MIN) {
        if (lastTerm) restore();
        return;
      }
      timer = window.setTimeout(function () { run(term); }, BLOG_SEARCH_DEBOUNCE);
    });

    input.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && input.value !== '') {
        event.preventDefault();
        input.value = '';
        restore();
      }
    });

    form.addEventListener('submit', function (event) {
      var term = input.value.trim();
      if (term.length < BLOG_SEARCH_MIN) return; // the search page handles short terms
      event.preventDefault();
      window.clearTimeout(timer);
      run(term);
    });

    clear.addEventListener('click', function () {
      input.value = '';
      restore();
      input.focus();
    });
  }

  function initBlogSearches(scope) {
    var parent = scope && scope.querySelectorAll ? scope : document;
    Array.prototype.forEach.call(parent.querySelectorAll('[data-vc-bloglist-search-form]'), initBlogSearch);
  }

  /* ------------------------------------------------------------------------
     G09 — Blog newsletter validation (analysis/blog-certificado-digital D13)
     Markup: sections/vcert-blog-newsletter.liquid
     The field is type=email + required, so without this the browser's own
     bubble validates. With it: novalidate, and on an invalid submit the
     reference's inline error — aria-invalid on the field, the message shown
     (tied by aria-describedby), focus back on the field. Typing clears it.
     The submit itself stays a native POST (Shopify's redirect and challenge
     page, D13).
     ---------------------------------------------------------------------- */

  function initBlogNewsletter(form) {
    if (form.getAttribute('data-vc-blog-newsletter-ready') === 'true') return;
    var input = form.querySelector('[data-vc-blog-newsletter-input]');
    var error = form.querySelector('[data-vc-blog-newsletter-error]');
    if (!input || !error) return;
    form.setAttribute('data-vc-blog-newsletter-ready', 'true');
    form.noValidate = true;

    function setInvalid(invalid) {
      if (invalid) input.setAttribute('aria-invalid', 'true');
      else input.removeAttribute('aria-invalid');
      error.hidden = !invalid;
    }

    form.addEventListener('submit', function (event) {
      input.value = input.value.trim();
      if (input.checkValidity()) return;
      event.preventDefault();
      setInvalid(true);
      input.focus();
    });

    input.addEventListener('input', function () {
      if (input.getAttribute('aria-invalid') === 'true') setInvalid(false);
    });
  }

  function initBlogNewsletters(scope) {
    var parent = scope && scope.querySelectorAll ? scope : document;
    Array.prototype.forEach.call(parent.querySelectorAll('form[data-vc-blog-newsletter]'), initBlogNewsletter);
  }

  /* ------------------------------------------------------------------------
     G04 — Blog tema select (analysis/blog-certificado-digital)
     Markup: sections/vcert-blog-listing.liquid   Styling: assets/vcert.css
     Below 80rem the tema links give way to a select whose option values are
     the same /tagged/ URLs; choosing one goes there. The links are the
     no-JavaScript path (the select is shown only under .js). On Windows and
     Linux an arrow key on a closed select changes its value, and so fires
     `change`: the page navigates on the first arrow press. On macOS the key
     opens the picker instead. The reference behaves the same; kept (D8).
     ---------------------------------------------------------------------- */

  function initBlogTema(select) {
    if (select.getAttribute('data-vc-bloglist-tema-ready') === 'true') return;
    select.setAttribute('data-vc-bloglist-tema-ready', 'true');
    select.addEventListener('change', function () {
      if (select.value) window.location.assign(select.value);
    });
  }

  function initBlogTemas(scope) {
    var parent = scope && scope.querySelectorAll ? scope : document;
    Array.prototype.forEach.call(parent.querySelectorAll('[data-vc-bloglist-tema]'), initBlogTema);
  }

  /* ------------------------------------------------------------------------
     M04 — Blog article share row (analysis/article-certificado-digital)
     (and M06's copy link to section buttons, which report the same way)
     Markup: sections/vcert-blog-article.liquid   Styling: assets/vcert.css
     LinkedIn, WhatsApp and the AI links are plain links and need nothing.
     Here: the copy-link button (hidden until this runs), the native share
     sheet where the device has one (it replaces the three buttons, as the
     reference), and the Gemini prompt, copied before its link opens.
     A copy is reported only when the clipboard took it — the exit banner's
     rule: no label claiming a copy that never happened.
     ---------------------------------------------------------------------- */

  function initArticleShare(root) {
    if (root.getAttribute('data-vc-barticle-share-ready') === 'true') return;
    root.setAttribute('data-vc-barticle-share-ready', 'true');

    var url = root.getAttribute('data-url') || window.location.href;
    var title = root.getAttribute('data-title') || document.title;
    var status = root.querySelector('[data-vc-barticle-share-status]');
    var copy = root.querySelector('[data-vc-barticle-copy]');
    var native = root.querySelector('[data-vc-barticle-native]');
    var canCopy = !!(navigator.clipboard && typeof navigator.clipboard.writeText === 'function');
    var timer;

    if (native && typeof navigator.share === 'function') {
      Array.prototype.forEach.call(root.querySelectorAll('.vc-barticle__share-btn'), function (btn) {
        btn.hidden = true;
      });
      native.hidden = false;
      native.addEventListener('click', function () {
        navigator.share({ title: title, url: url }).catch(function () {});
      });
      return;
    }

    if (!copy || !canCopy) return;
    copy.hidden = false;
    var tooltip = copy.getAttribute('data-vc-tooltip') || '';
    var copied = copy.getAttribute('data-copied') || tooltip;

    copy.addEventListener('click', function () {
      navigator.clipboard.writeText(url).then(function () {
        copy.setAttribute('data-vc-tooltip', copied);
        copy.setAttribute('data-state', 'copied');
        if (status) status.textContent = copied;
        window.clearTimeout(timer);
        timer = window.setTimeout(function () {
          copy.setAttribute('data-vc-tooltip', tooltip);
          copy.removeAttribute('data-state');
          if (status) status.textContent = '';
        }, 2000);
      }).catch(function () {});
    });
  }

  function initArticleGemini(link) {
    if (link.getAttribute('data-vc-barticle-gemini-ready') === 'true') return;
    link.setAttribute('data-vc-barticle-gemini-ready', 'true');
    link.addEventListener('click', function () {
      var prompt = link.getAttribute('data-prompt');
      if (prompt && navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        navigator.clipboard.writeText(prompt).catch(function () {});
      }
    });
  }

  // M06: the copy link button on every h2 of the body (ids built in Liquid,
  // D3). Copies the article's public URL with the heading's #id; reported in
  // the share row's status region, under the same rule as copy link.
  function initArticleAnchors(wrap) {
    if (wrap.getAttribute('data-vc-barticle-anchors-ready') === 'true') return;
    wrap.setAttribute('data-vc-barticle-anchors-ready', 'true');
    if (!navigator.clipboard || typeof navigator.clipboard.writeText !== 'function') return;

    var share = wrap.querySelector('[data-vc-barticle-share]');
    var base = (share && share.getAttribute('data-url')) || window.location.href.split('#')[0];
    var status = wrap.querySelector('[data-vc-barticle-share-status]');
    var copied = wrap.getAttribute('data-section-copied') || '';
    var timer;

    Array.prototype.forEach.call(wrap.querySelectorAll('[data-vc-barticle-anchor]'), function (button) {
      button.hidden = false;
      button.addEventListener('click', function () {
        var url = base + '#' + button.getAttribute('data-vc-barticle-anchor');
        navigator.clipboard.writeText(url).then(function () {
          Array.prototype.forEach.call(wrap.querySelectorAll('.vc-barticle__copy[data-state]'), function (b) {
            b.removeAttribute('data-state');
          });
          button.setAttribute('data-state', 'copied');
          if (status) status.textContent = copied;
          window.clearTimeout(timer);
          timer = window.setTimeout(function () {
            button.removeAttribute('data-state');
            if (status) status.textContent = '';
          }, 2000);
        }).catch(function () {});
      });
    });
  }

  /* ------------------------------------------------------------------------
     M07 — Blog article TOC (analysis/article-certificado-digital)
     Markup: sections/vcert-blog-article.liquid   Styling: assets/vcert.css
     The drawer below 64rem is F05's, unchanged. Here: the current section,
     as the reference computes it — the last h2 whose top has passed the
     sticky bar's bottom + 120px — marked aria-current="location" and kept in
     view inside the sidebar when the list scrolls; and a link chosen in the
     open drawer closes it before going to its section.
     ---------------------------------------------------------------------- */

  function initArticleToc(nav) {
    if (nav.getAttribute('data-vc-toc-ready') === 'true') return;
    nav.setAttribute('data-vc-toc-ready', 'true');

    var links = Array.prototype.slice.call(nav.querySelectorAll('[data-vc-toc-link]'));
    var headings = links.map(function (link) {
      return document.getElementById(link.getAttribute('data-vc-toc-link'));
    }).filter(Boolean);
    if (!headings.length) return;

    var header = document.querySelector('.vc-header');
    var current = null;
    var queued = false;

    // The sidebar's <nav> is its own scroll box from 64rem (sticky, capped
    // to the screen); in the drawer it is not, and nothing scrolls here.
    function keepInView(link) {
      var box = nav;
      if (!nav.closest('.vc-barticle__aside') || box.scrollHeight <= box.clientHeight) return;
      var b = box.getBoundingClientRect();
      var l = link.getBoundingClientRect();
      if (l.top < b.top) box.scrollTop -= b.top - l.top + 8;
      else if (l.bottom > b.bottom) box.scrollTop += l.bottom - b.bottom + 8;
    }

    function update() {
      queued = false;
      var line = (header ? Math.max(0, header.getBoundingClientRect().bottom) : 0) + 120;
      var id = null;
      for (var i = 0; i < headings.length; i++) {
        if (headings[i].getBoundingClientRect().top <= line) id = headings[i].id;
        else break;
      }
      if (id === current) return;
      current = id;
      links.forEach(function (link) {
        if (link.getAttribute('data-vc-toc-link') === id) {
          link.setAttribute('aria-current', 'location');
          keepInView(link);
        } else {
          link.removeAttribute('aria-current');
        }
      });
    }

    function queue() {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(update);
    }

    window.addEventListener('scroll', queue, { passive: true });
    window.addEventListener('resize', queue);
    update();

    nav.addEventListener('click', function (event) {
      var link = event.target.closest('[data-vc-toc-link]');
      var drawer = link && link.closest('dialog[open]');
      if (!drawer) return;
      event.preventDefault();
      drawer.close();
      window.location.hash = link.getAttribute('data-vc-toc-link');
    });
  }

  function initArticleShares(scope) {
    var parent = scope && scope.querySelectorAll ? scope : document;
    Array.prototype.forEach.call(parent.querySelectorAll('[data-vc-barticle-share]'), initArticleShare);
    Array.prototype.forEach.call(parent.querySelectorAll('[data-vc-barticle-gemini]'), initArticleGemini);
    Array.prototype.forEach.call(parent.querySelectorAll('[data-vc-barticle]'), initArticleAnchors);
    Array.prototype.forEach.call(parent.querySelectorAll('[data-vc-toc]'), initArticleToc);
  }

  // The theme editor replaces a section's DOM wholesale, so the replacement
  // arrives without the ready flag and simply initialises again.
  document.addEventListener('shopify:section:load', function (event) {
    initCarousels(event.target);
    initStats(event.target);
    initMegas(event.target);
    initDrawers(event.target);
    initSearchPanels(event.target);
    initCart(event.target);
    initBackToTops(event.target);
    initExitBanners(event.target);
    initAboutVideos(event.target);
    initCatResults(event.target);
    initCatAbouts(event.target);
    initSearchMores(event.target);
    initFaqBars(event.target);
    initFaqSearches(event.target);
    initChatOpeners(event.target);
    initFaqNavDrawers(event.target);
    initConsentGates(event.target);
    initBlogTemas(event.target);
    initBlogBanners(event.target);
    initBlogSearches(event.target);
    initBlogNewsletters(event.target);
    initArticleShares(event.target);
  });

  initCarousels(document);
  initStats(document);
  initMegas(document);
  initDrawers(document);
  initSearchPanels(document);
  initCart(document);
  initBackToTops(document);
  initExitBanners(document);
  initAboutVideos(document);
  initCatResults(document);
  initCatAbouts(document);
  initSearchMores(document);
  initFaqBars(document);
  initFaqSearches(document);
  initChatOpeners(document);
  initFaqNavDrawers(document);
  initConsentGates(document);
  initBlogTemas(document);
  initBlogBanners(document);
  initBlogSearches(document);
  initBlogNewsletters(document);
  initArticleShares(document);
})();
